import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AppLayout } from '../components/AppLayout';
import { PageHeader } from '../components/ds';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Popover, PopoverTrigger, PopoverContent } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import { Tooltip as UiTooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../components/ui/tooltip';
import { toast } from 'sonner';
import {
  Upload, Search, TrendingUp, TrendingDown, Minus, ArrowUpDown, Loader2,
  CheckCircle2, XCircle, AlertTriangle, FileSpreadsheet, FileText, Store,
  Table as TableIcon, LayoutGrid, Users, Ruler, Trophy, Filter, RotateCcw, Euro,
  Crown, Medal, History, Eye, EyeOff, Columns3, Info, MapPin, X, Send,
  ShieldCheck, CalendarClock, Pencil, Check, ChevronUp, ChevronDown, Flag, Scale,
} from 'lucide-react';
import { TensionDialog } from '../components/cacorner/TensionDialog';
import { TensionTab } from '../components/cacorner/TensionTab';
import { ParcAnalysisTab } from '../components/cacorner/ParcAnalysisTab';
import { PerformanceTab } from '../components/cacorner/PerformanceTab';
import { TensionPill } from '../components/cacorner/TensionPill';
import { TENSION_LEVELS } from '../components/cacorner/tensionMeta';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell,
  LineChart, Line, ReferenceLine, Legend,
} from 'recharts';
import { FRANCE_PROJ, DEPARTEMENTS } from '../data/franceDepartements';

const ZONES = {
  NO: { label: 'Nord-Ouest', color: '#2563eb', dot: '#3b82f6', rowHl: 'bg-blue-100 dark:bg-blue-900/30' },
  NE: { label: 'Nord-Est', color: '#7c3aed', dot: '#8b5cf6', rowHl: 'bg-violet-100 dark:bg-violet-900/30' },
  SO: { label: 'Sud-Ouest', color: '#059669', dot: '#10b981', rowHl: 'bg-emerald-100 dark:bg-emerald-900/30' },
  SE: { label: 'Sud-Est', color: '#d97706', dot: '#f59e0b', rowHl: 'bg-amber-100 dark:bg-amber-900/30' },
};
const ZONE_ORDER = ['NO', 'NE', 'SO', 'SE'];

const ALL = '__all__';

function CountUp({ value, format, duration = 1000, delay = 0 }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    let raf, start;
    const to = Number(value) || 0;
    const step = (t) => {
      if (start === undefined) start = t;
      const prog = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - prog, 3);
      setDisplay(to * eased);
      if (prog < 1) raf = requestAnimationFrame(step);
    };
    const timer = setTimeout(() => { raf = requestAnimationFrame(step); }, delay);
    return () => { clearTimeout(timer); if (raf) cancelAnimationFrame(raf); };
  }, [value, duration, delay]);
  return <>{format(display)}</>;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MONTHS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

const fmtEur = (n) => (n === null || n === undefined || n === 0) ? 'NC' : `${Number(n).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €`;
const fmtText = (s) => (s === null || s === undefined || s === '') ? '—' : s;
const fmtSurface = (n) => (n === null || n === undefined) ? '—' : `${Number(n).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} m²`;
const fmtTaux = (n) => (n === null || n === undefined) ? '—' : `${Number(n).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} %`;
const TVA_RATE = 1.10;
const ttc = (v) => (v === null || v === undefined) ? null : v * TVA_RATE;
const caTtcM2 = (c) => (c && c.surface_m2 && c.ca_ht_monthly !== null && c.ca_ht_monthly !== undefined) ? ttc(c.ca_ht_monthly) / c.surface_m2 : null;
const fmtEurM2 = (n) => (n === null || n === undefined || n === 0) ? 'NC' : `${Number(n).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €/m²`;

// Logos des marques du réseau WASABI — correspondance sur la colonne "Marque" (import Excel).
const BRAND_LOGOS = { WASABI: '/logos/wasabi.png', SORAE: '/logos/sorae.png' };
const brandLogo = (marque) => BRAND_LOGOS[(marque || 'WASABI').toUpperCase()] || BRAND_LOGOS.WASABI;
const BrandLogoCell = ({ marque }) => {
  const m = (marque || 'WASABI').toUpperCase();
  return (
    <img src={brandLogo(m)} alt={m} title={m}
      className="w-7 h-7 object-contain mx-auto rounded" data-testid={`ca-brand-logo-${m}`} />
  );
};

// Moyenne qualité (audits) — code couleur : vert ≥95 %, orange 90–94.99 %, rouge <90 %.
const qualityColorClasses = (v) => {
  if (v === null || v === undefined) return 'bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500';
  if (v >= 95) return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300';
  if (v >= 90) return 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300';
  return 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';
};
const fmtQuality = (v) => (v === null || v === undefined) ? '—' : `${Number(v).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %`;
const QualityBadge = ({ value, detail }) => {
  const badge = (
    <span
      className={`inline-flex items-center justify-center min-w-[58px] px-2 py-0.5 rounded-full text-xs font-semibold font-mono tabular-nums ${qualityColorClasses(value)} ${detail ? 'cursor-help' : ''}`}
      data-testid="ca-quality-badge">
      {fmtQuality(value)}
    </span>
  );
  if (!detail) return badge;
  return (
    <TooltipProvider delayDuration={100}>
      <UiTooltip>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        <TooltipContent side="top" className="max-w-[320px] text-xs leading-relaxed normal-case font-normal">
          <span className="font-semibold block mb-0.5">Audits qualité</span>
          {detail}
        </TooltipContent>
      </UiTooltip>
    </TooltipProvider>
  );
};

// Parse le texte "Janv 94.4% · Fév 91.7% · Mai 98.1% …" en série [{month, value}].
const parseQualityHistory = (detail) => {
  if (!detail || typeof detail !== 'string') return [];
  const out = [];
  const re = /([A-Za-zÀ-ÿ.éû]+)\s*[:\-]?\s*(\d+(?:[.,]\d+)?)\s*%/g;
  let m;
  while ((m = re.exec(detail)) !== null) {
    const month = m[1].replace(/\.$/, '');
    const value = parseFloat(m[2].replace(',', '.'));
    if (!Number.isNaN(value)) out.push({ month, value });
  }
  return out;
};

const MONTH_KEYS = ['janv', 'fev', 'mars', 'avr', 'mai', 'juin', 'juil', 'aout', 'sept', 'oct', 'nov', 'dec'];
const MONTH_LABELS = ['Janv', 'Fév', 'Mars', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sept', 'Oct', 'Nov', 'Déc'];
const monthIndex = (label) => {
  const m = String(label || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  return MONTH_KEYS.findIndex(k => m.startsWith(k.slice(0, k.startsWith('jui') ? 4 : 3)));
};
const zoneOfValue = (q) => (q == null ? null : q >= 95 ? 'green' : q >= 90 ? 'orange' : 'red');

const qualityDot = (v) => (v >= 95 ? '#25272B' : (v >= 90 ? '#F26A1B' : '#B42318'));

const QualityHistoryChart = ({ detail, average }) => {
  const series = React.useMemo(() => parseQualityHistory(detail), [detail]);
  if (series.length === 0) {
    return <p className="text-sm text-slate-400 italic" data-testid="ca-quality-history-empty">Aucun détail d'audits disponible pour ce corner.</p>;
  }
  const vals = series.map(s => s.value);
  const minV = Math.max(0, Math.floor(Math.min(...vals, 90) / 5) * 5 - 5);
  return (
    <div>
      <div className="h-52 w-full" data-testid="ca-quality-history-chart">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={series} margin={{ top: 10, right: 16, left: -8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748b' }} />
            <YAxis domain={[minV, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 10, fill: '#64748b' }} width={44} />
            <Tooltip formatter={(v) => [`${Number(v).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %`, 'Qualité']} />
            <ReferenceLine y={95} stroke="#25272B" strokeDasharray="4 4" strokeWidth={1}
              label={{ value: '95%', position: 'right', fontSize: 9, fill: '#25272B' }} />
            <ReferenceLine y={90} stroke="#f97316" strokeDasharray="4 4" strokeWidth={1}
              label={{ value: '90%', position: 'right', fontSize: 9, fill: '#f97316' }} />
            <Line type="monotone" dataKey="value" stroke="#6366f1" strokeWidth={2}
              dot={(props) => {
                const { cx, cy, payload, index } = props;
                return <circle key={index} cx={cx} cy={cy} r={4} fill={qualityDot(payload.value)} stroke="white" strokeWidth={1.5} />;
              }}
              activeDot={{ r: 6 }} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="flex items-center justify-between mt-2 text-xs text-slate-500">
        <span>{series.length} audit(s)</span>
        {average != null && (
          <span className="inline-flex items-center gap-1.5">Moyenne <QualityBadge value={average} /></span>
        )}
      </div>
    </div>
  );
};


const InfoTip = ({ text }) => (
  <UiTooltip>
    <TooltipTrigger asChild>
      <span className="inline-flex cursor-help text-slate-400 hover:text-indigo-600 align-middle"><Info className="w-3.5 h-3.5" /></span>
    </TooltipTrigger>
    <TooltipContent side="top" className="max-w-[260px] text-xs leading-relaxed normal-case font-normal">{text}</TooltipContent>
  </UiTooltip>
);

const StatBox = ({ label, value, tip, valueClass }) => (
  <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4">
    <p className="text-xs uppercase text-slate-400 mb-1 flex items-center gap-1">{label}{tip && <InfoTip text={tip} />}</p>
    <p className={`text-xl font-bold font-mono ${valueClass || ''}`}>{value}</p>
  </div>
);
const parseFRDate = (s) => {
  if (!s) return null;
  const str = String(s).trim();
  let m = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  m = str.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})/);
  if (m) return new Date(+m[3], +m[2] - 1, +m[1]);
  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
};
const toISODate = (s) => { const d = parseFRDate(s); if (!d) return ''; const p = (n) => String(n).padStart(2, '0'); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`; };
const fmtDateFR = (s) => { const d = parseFRDate(s); return d ? d.toLocaleDateString('fr-FR') : '—'; };
// Vrai si la Date FDC est déjà dépassée (contrat échu) — comparaison au jour même.
const isFdcPast = (s) => { const d = parseFRDate(s); if (!d) return false; const t = new Date(); t.setHours(0, 0, 0, 0); return d < t; };
const monthsElapsed = (c) => {
  const d = parseFRDate(c.date_contrat);
  if (!d || !c.ca_n_month) return null;
  const yr = c.ca_n_year || new Date().getFullYear();
  const me = (yr - d.getFullYear()) * 12 + (c.ca_n_month - (d.getMonth() + 1)) + 1;
  return Math.min(12, Math.max(1, me));
};
const daysInYear = (y) => (((y % 4 === 0 && y % 100 !== 0) || y % 400 === 0) ? 366 : 365);
// Run-rate annualisé précis (au jour près).
// Départ = date d'ouverture (1er contrat) si ouvert dans l'année N ; sinon 1er janvier N
// (notamment si un CA N-1 existe → le CA est réputé généré depuis le 1er janvier).
export const runRateAnnual = (c) => {
  if (!c || c.ca_n_current == null || !c.ca_n_month) return null;
  const yr = c.ca_n_year || new Date().getFullYear();
  const startOfYear = new Date(yr, 0, 1);
  const openDate = parseFRDate(c.date_contrat);
  const endPeriod = new Date(yr, c.ca_n_month, 0); // dernier jour du mois cumulé
  let start = startOfYear;
  // On ne prend la date d'ouverture comme départ que si elle est cohérente :
  // dans l'année N, après le 1er janvier ET au plus tard le dernier jour de la période cumulée.
  // (Si la date de 1er contrat est postérieure à la période, on retombe sur le 1er janvier
  // pour éviter un run-rate aberrant dû à un dénominateur de 1 jour.)
  if (!c.ca_n1 && openDate && openDate.getFullYear() === yr && openDate > startOfYear && openDate <= endPeriod) {
    start = openDate;
  }
  const daysElapsed = Math.max(1, Math.round((endPeriod - start) / 86400000) + 1);
  if (c.ca_n_current <= 0) return null;
  return (c.ca_n_current / daysElapsed) * daysInYear(yr);
};
// Incohérence : Date de 1er contrat postérieure au dernier jour du mois de CA importé.
const contractAfterPeriod = (c) => {
  const d = parseFRDate(c && c.date_contrat);
  if (!d || !c || !c.ca_n_month) return false;
  const yr = c.ca_n_year || new Date().getFullYear();
  const endPeriod = new Date(yr, c.ca_n_month, 0);
  return d > endPeriod;
};
const fmtPct1 = (n) => (n === null || n === undefined) ? 'NC' : `${n > 0 ? '+' : ''}${n.toFixed(1)}%`;

// Objectifs des ratios opérationnels (rappel affiché en tooltip).
const RATIO_OBJECTIVES = { foodcost: 24, ratio_saumon: 7.5, taux_casse: 10 };
// Badge coloré : par défaut VERT si ≤ objectif. Si higherIsBetter, VERT si ≥ objectif.
const RatioBadge = ({ value, objective, digits = 1, higherIsBetter = false, suffix = ' %' }) => {
  if (value === null || value === undefined) return <span className="text-slate-400">—</span>;
  const ok = higherIsBetter ? Number(value) >= objective : Number(value) <= objective;
  const cls = ok
    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
    : 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-300';
  const objLabel = higherIsBetter ? `≥ ${objective.toLocaleString('fr-FR')}${suffix}` : `≤ ${objective.toLocaleString('fr-FR')}${suffix}`;
  return (
    <span title={`Objectif : ${objLabel}`}
      className={`inline-flex items-center justify-center min-w-[56px] px-2 py-0.5 rounded-full text-xs font-semibold font-mono tabular-nums cursor-help ${cls}`}>
      {Number(value).toLocaleString('fr-FR', { maximumFractionDigits: digits })}{suffix}
    </span>
  );
};

// Brique qualité : nombre de corners par zone (moyenne d'audits), sur tout le parc, toujours visible.
const QUALITY_ZONES = [
  { key: 'green', label: '≥ 95 %', caption: 'Conformes', dot: 'bg-emerald-500', ring: 'ring-emerald-300', text: 'text-emerald-700' },
  { key: 'orange', label: '90 – 94,99 %', caption: 'À surveiller', dot: 'bg-orange-500', ring: 'ring-orange-300', text: 'text-orange-700' },
  { key: 'red', label: '< 90 %', caption: 'Sous le seuil', dot: 'bg-red-600', ring: 'ring-red-300', text: 'text-red-700' },
];
const TREND_GOOD_WHEN_UP = { green: true, orange: false, red: false };
const QualityTrend = ({ zone, cur, trend }) => {
  if (!trend) return null;
  const prev = trend.prev[zone];
  const diff = cur - prev;
  const pctTxt = prev ? `${diff >= 0 ? '+' : ''}${(diff / prev * 100).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %` : (diff ? `+${diff}` : '0 %');
  const good = diff === 0 ? null : (diff > 0) === TREND_GOOD_WHEN_UP[zone];
  const cls = good == null ? 'text-slate-500' : good ? 'text-emerald-600' : 'text-red-600';
  const Icon = diff > 0 ? TrendingUp : diff < 0 ? TrendingDown : Minus;
  return (
    <span className={`flex items-center gap-1 text-[11px] font-semibold ${cls}`} title={`Fin ${trend.prevLabel} : ${prev} corner(s) → après les audits de ${trend.refLabel} : ${cur}`} data-testid={`quality-brick-${zone}-trend`}>
      <Icon className="w-3 h-3" /> {pctTxt} <span className="font-normal text-slate-400">vs {trend.prevLabel} ({prev})</span>
    </span>
  );
};
function QualityBrick({ stats, trend, active, onPick, children }) {
  const pct = (n) => `${(n / stats.n * 100).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} %`;
  return (
    <div className="mb-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-4 py-3 flex flex-wrap items-center gap-4" data-testid="ca-corner-quality-brick">
      <div className="flex items-center gap-2 min-w-[180px]">
        <ShieldCheck className="w-5 h-5 text-emerald-600" />
        <div>
          <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">Qualité du parc</p>
          <p className="text-xs text-slate-500" data-testid="quality-brick-avg">Moyenne {stats.avg.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} % · {stats.n} corner(s) audité(s)</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-3 flex-1">
        {QUALITY_ZONES.map(z => (
          <button key={z.key} type="button" onClick={() => onPick(z.key)} title="Cliquer pour surligner ces corners dans le tableau"
            className={`flex items-center gap-3 rounded-xl border border-slate-200 dark:border-slate-700 px-4 py-2 transition-shadow hover:shadow-sm ${active === z.key ? `ring-2 ${z.ring}` : ''}`}
            data-testid={`quality-brick-${z.key}`}>
            <span className={`w-11 h-11 rounded-full ${z.dot} text-white text-lg font-bold tabular-nums flex items-center justify-center`} data-testid={`quality-brick-${z.key}-count`}>{stats[z.key]}</span>
            <span className="text-left">
              <span className={`block text-sm font-semibold ${z.text}`}>{z.label}</span>
              <span className="block text-xs text-slate-500">{z.caption} · {pct(stats[z.key])}</span>
              <QualityTrend zone={z.key} cur={stats[z.key]} trend={trend} />
            </span>
          </button>
        ))}
      </div>
      {children}
    </div>
  );
}

export default function CaCornerPage() {
  const { token, user } = useAuth();
  const isAdmin = ['admin', 'super_admin'].includes(user?.role);
  const canEdit = ['admin', 'super_admin', 'commercial'].includes(user?.role);
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [corners, setCorners] = useState([]);
  const [hiddenNoCa, setHiddenNoCa] = useState(0);
  const [visibleCols, setVisibleCols] = useState(() => {
    try { const s = JSON.parse(localStorage.getItem('caCornerCols') || 'null'); if (s && typeof s === 'object') return s; } catch (e) { /* ignore */ }
    return {};
  });
  const toggleCol = (key) => setVisibleCols(prev => {
    const next = { ...prev, [key]: !(prev[key] !== false) };
    try { localStorage.setItem('caCornerCols', JSON.stringify(next)); } catch (e) { /* ignore */ }
    return next;
  });
  const resetCols = () => {
    setVisibleCols({});
    try { localStorage.removeItem('caCornerCols'); } catch (e) { /* ignore */ }
  };
  const [selectedZone, setSelectedZone] = useState(null);
  const [zoneDialog, setZoneDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('name_powerbi');
  const [sortDir, setSortDir] = useState('asc');
  const [lastImport, setLastImport] = useState(null);

  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() === 0 ? 12 : now.getMonth()); // M-1
  const [year, setYear] = useState(now.getFullYear());
  const [selectedFile, setSelectedFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [validating, setValidating] = useState(false);
  const [preview, setPreview] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [unknownSkip, setUnknownSkip] = useState(new Set());
  const [detailCorner, setDetailCorner] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [view, setView] = useState('table');
  const [filterDr, setFilterDr] = useState(ALL);
  const [filterBm, setFilterBm] = useState(ALL);
  const [filterTrend, setFilterTrend] = useState(ALL);
  const [filterQuality, setFilterQuality] = useState(ALL);
  const [qualityThreshold, setQualityThreshold] = useState(90);
  const [fdcHighlight, setFdcHighlight] = useState([]);
  const toggleFdc = (k) => setFdcHighlight(prev => (prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k]));
  const [qualityHighlight, setQualityHighlight] = useState(null);
  const [fdcCustomYear, setFdcCustomYear] = useState('');
  const [fdcEditing, setFdcEditing] = useState(null);
  const [showKpi, setShowKpi] = useState(false);
  const [sendingQualityAlert, setSendingQualityAlert] = useState(false);
  const [podiumMetric, setPodiumMetric] = useState('ca');
  const [lastReport, setLastReport] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [responsables, setResponsables] = useState([]);
  const [alertLog, setAlertLog] = useState([]);
  const [alertLogLoading, setAlertLogLoading] = useState(false);
  const [rrCornerId, setRrCornerId] = useState('');
  const [rrData, setRrData] = useState(null);
  const [rrLoading, setRrLoading] = useState(false);

  const authHeaders = { 'Authorization': `Bearer ${token}` };

  const [tensions, setTensions] = useState([]);
  const [tensionMotifs, setTensionMotifs] = useState([]);
  const [canEditTension, setCanEditTension] = useState(false);
  const [tensionCorner, setTensionCorner] = useState(null);
  const loadTensions = () => fetch(`${BACKEND_URL}/api/ca-corner/tensions`, { headers: authHeaders })
    .then(r => (r.ok ? r.json() : null))
    .then(d => { if (d) { setTensions(d.tensions || []); setTensionMotifs(d.motifs || []); setCanEditTension(!!d.can_edit); } })
    .catch(() => {});
  useEffect(() => { loadTensions(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const tensionByCorner = useMemo(() => Object.fromEntries(tensions.map(t => [t.corner_id, t])), [tensions]);
  const activeTensionCount = tensions.filter(t => t.active).length;

  const loadData = async () => {
    setLoading(true);
    try {
      const [cRes, lRes, uRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/ca-corner/corners`, { headers: authHeaders }),
        fetch(`${BACKEND_URL}/api/ca-corner/last-import`, { headers: authHeaders }),
        fetch(`${BACKEND_URL}/api/users/list`, { headers: authHeaders }),
      ]);
      fetch(`${BACKEND_URL}/api/ca-corner/quality-alerts/preview`, { headers: authHeaders })
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d && d.threshold_pct != null) setQualityThreshold(d.threshold_pct); })
        .catch(() => {});
      if (cRes.ok) { const d = await cRes.json(); setCorners(d.corners || []); setHiddenNoCa(d.closed_count || 0); }
      if (lRes.ok) { const d = await lRes.json(); setLastImport(d && d.month_label ? d : null); }
      if (uRes.ok) {
        const list = await uRes.json();
        const allowed = ['super_admin', 'admin', 'commercial', 'business_manager', 'directeur_regional'];
        setResponsables((list || []).filter(u => allowed.includes(u.role)));
      }
    } catch (e) { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  };
  const [geocoding, setGeocoding] = useState(false);
  const handleGeocode = async () => {
    setGeocoding(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/ca-corner/geocode?force=true`, { method: 'POST', headers: authHeaders });
      if (!res.ok) throw new Error();
      const d = await res.json();
      toast.success(`Carte actualisée : ${d.geocoded}/${d.total} corners localisés`);
      await loadData();
    } catch (e) { toast.error('Échec de l\'actualisation de la carte'); }
    finally { setGeocoding(false); }
  };

  useEffect(() => { loadData(); /* eslint-disable-next-line */ }, []);

  const handleAnalyze = async () => {
    if (!selectedFile) { toast.error('Sélectionnez un fichier Excel'); return; }
    setAnalyzing(true); setPreview(null);
    try {
      const fd = new FormData();
      fd.append('file', selectedFile);
      fd.append('month', String(month));
      fd.append('year', String(year));
      const res = await fetch(`${BACKEND_URL}/api/ca-corner/import/preview`, { method: 'POST', headers: authHeaders, body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur');
      setPreview(data);
      setUnknownSkip(new Set());
      toast.success('Fichier analysé — vérifiez les contrôles avant validation');
    } catch (e) { toast.error(e.message || "Erreur lors de l'analyse"); }
    finally { setAnalyzing(false); }
  };

  const handleValidate = async () => {
    if (!preview) return;
    setValidating(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/ca-corner/import/${preview.import_id}/validate`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ skip_unknown: Array.from(unknownSkip) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur');
      const rep = data.report || null;
      setLastReport(rep && rep.not_imported_count > 0 ? { ...rep, month_label: preview.month_label, year: preview.year } : null);
      const niTxt = rep && rep.not_imported_count ? `, ${rep.not_imported_count} non importée(s)` : '';
      toast.success(`Import validé : ${data.created} créé(s), ${data.updated} mis à jour${data.skipped ? `, ${data.skipped} ignoré(s)` : ''}${niTxt}`);
      setPreview(null); setSelectedFile(null); setUnknownSkip(new Set()); if (fileRef.current) fileRef.current.value = '';
      loadData();
    } catch (e) { toast.error(e.message || 'Erreur de validation'); }
    finally { setValidating(false); }
  };

  const handleCancelPreview = async () => {
    if (preview?.import_id) {
      try { await fetch(`${BACKEND_URL}/api/ca-corner/import/${preview.import_id}`, { method: 'DELETE', headers: authHeaders }); } catch {}
    }
    setPreview(null); setSelectedFile(null); if (fileRef.current) fileRef.current.value = '';
  };

  const handleExport = async (kind) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/ca-corner/export/${kind}`, { headers: authHeaders });
      if (!res.ok) throw new Error('Export échoué');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CA_CORNER_${lastImport ? (lastImport.month_label + '_' + lastImport.year) : 'export'}.${kind === 'excel' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(a); a.click(); window.URL.revokeObjectURL(url); a.remove();
      toast.success('Export téléchargé');
    } catch (e) { toast.error("Erreur lors de l'export"); }
  };

  const handleExportFacturation = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/ca-corner/export/facturation-excel`, { headers: authHeaders });
      if (!res.ok) throw new Error('Export échoué');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `SUIVI_FACTURATION_${lastImport ? (lastImport.month_label + '_' + lastImport.year) : 'export'}.xlsx`;
      document.body.appendChild(a); a.click(); window.URL.revokeObjectURL(url); a.remove();
      toast.success('Export téléchargé');
    } catch (e) { toast.error("Erreur lors de l'export"); }
  };
  const buildReportCsv = (notImported, label) => {
    const header = 'Ligne;Corner;Raison\n';
    const body = (notImported || []).map(r =>
      `${r.row ?? ''};"${(r.name || 'nom absent').replace(/"/g, '""')}";"${(r.reason || '').replace(/"/g, '""')}"`
    ).join('\n');
    const blob = new Blob(['\ufeff' + header + body], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Rapport_import_CA_CORNER_${label}.csv`;
    document.body.appendChild(a); a.click(); window.URL.revokeObjectURL(url); a.remove();
  };

  const downloadReportCsv = () => {
    if (!lastReport) return;
    buildReportCsv(lastReport.not_imported, `${lastReport.month_label}_${lastReport.year}`);
  };

  const openHistory = async () => {
    setHistoryOpen(true); setHistoryLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/ca-corner/imports`, { headers: authHeaders });
      if (res.ok) { const d = await res.json(); setHistory(d.imports || []); }
      else throw new Error();
    } catch { toast.error("Erreur lors du chargement de l'historique"); }
    finally { setHistoryLoading(false); }
  };

  const handleRowClick = async (id) => {
    setDetailOpen(true);
    setDetailCorner(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/ca-corner/corners/${id}`, { headers: authHeaders });
      if (res.ok) setDetailCorner(await res.json());
    } catch { toast.error('Erreur de chargement du détail'); }
  };

  const toggleUnknown = (name) => {
    setUnknownSkip(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir(key === 'name_powerbi' || key === 'name_sf' ? 'asc' : 'desc'); }
  };

  const drOptions = useMemo(() => Array.from(new Set(corners.map(c => c.nom_dr).filter(Boolean))).sort((a, b) => a.localeCompare(b)), [corners]);
  const bmOptions = useMemo(() => Array.from(new Set(corners.map(c => c.nom_bm).filter(Boolean))).sort((a, b) => a.localeCompare(b)), [corners]);
  const filtersActive = filterDr !== ALL || filterBm !== ALL || filterTrend !== ALL || filterQuality !== ALL || search.trim() !== '';
  const resetFilters = () => { setFilterDr(ALL); setFilterBm(ALL); setFilterTrend(ALL); setFilterQuality(ALL); setSearch(''); };

  // Filtre sur la moyenne qualité : vert ≥95, orange 90–94.99, rouge <90, sans audit = null.
  const matchQuality = (c) => {
    if (filterQuality === ALL) return true;
    const q = c.quality_avg;
    if (filterQuality === 'green') return q != null && q >= 95;
    if (filterQuality === 'orange') return q != null && q >= 90 && q < 95;
    if (filterQuality === 'red') return q != null && q < 90;
    if (filterQuality === 'none') return q == null;
    return true;
  };

  const filtered = useMemo(() => {
    let list = corners;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(c => (c.name_powerbi || '').toLowerCase().includes(q) || (c.name_sf || '').toLowerCase().includes(q));
    }
    if (filterDr !== ALL) list = list.filter(c => (c.nom_dr || '') === filterDr);
    if (filterBm !== ALL) list = list.filter(c => (c.nom_bm || '') === filterBm);
    if (filterTrend !== ALL) {
      list = list.filter(c => {
        const t = c.trend;
        if (filterTrend === 'up') return t !== null && t !== undefined && t > 0;
        if (filterTrend === 'down') return t !== null && t !== undefined && t < 0;
        if (filterTrend === 'nc') return t === null || t === undefined;
        return true;
      });
    }
    if (filterQuality !== ALL) list = list.filter(matchQuality);
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      let av = sortKey === 'ca_ttc_m2' ? caTtcM2(a) : a[sortKey];
      let bv = sortKey === 'ca_ttc_m2' ? caTtcM2(b) : b[sortKey];
      if (typeof av === 'string' || typeof bv === 'string') {
        return (av || '').toString().localeCompare((bv || '').toString()) * dir;
      }
      av = av ?? -Infinity; bv = bv ?? -Infinity;
      return (av - bv) * dir;
    });
  }, [corners, search, sortKey, sortDir, filterDr, filterBm, filterTrend, filterQuality]);

  // Moyennes du parc (sur les corners actuellement affichés).
  const parkAverages = useMemo(() => {
    const rows = filtered;
    // Surface moyenne : on exclut les corners sans surface renseignée (0 m² ou vide).
    const surf = rows.filter(c => Number(c.surface_m2) > 0);
    const avgSurf = surf.length ? surf.reduce((s, c) => s + Number(c.surface_m2), 0) / surf.length : null;
    const mo = rows.filter(c => c.ca_ht_monthly != null && c.ca_ht_monthly !== 0);
    const avgMoTtc = mo.length ? mo.reduce((s, c) => s + ttc(c.ca_ht_monthly), 0) / mo.length : null;
    const n1 = rows.filter(c => c.ca_n1);
    const avgN1 = n1.length ? n1.reduce((s, c) => s + c.ca_n1, 0) / n1.length : null;
    const n2 = rows.filter(c => c.ca_n2);
    const avgN2 = n2.length ? n2.reduce((s, c) => s + c.ca_n2, 0) / n2.length : null;
    // CA au m² : moyenne des ratios PAR CORNER (robuste aux surfaces aberrantes).
    const perCorner = (key) => {
      const vals = rows.filter(c => Number(c.surface_m2) > 0 && c[key]).map(c => c[key] / Number(c.surface_m2));
      return vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
    };
    const monthVals = rows.filter(c => Number(c.surface_m2) > 0 && c.ca_ht_monthly).map(c => ttc(c.ca_ht_monthly) / Number(c.surface_m2));
    const caM2Monthly = monthVals.length ? monthVals.reduce((s, v) => s + v, 0) / monthVals.length : null;
    const caM2N1 = perCorner('ca_n1');
    const caM2N2 = perCorner('ca_n2');
    const caM2Annual = caM2Monthly != null ? caM2Monthly * 12 : null;
    const caM2AnnualVsN1 = (caM2Annual != null && caM2N1) ? (caM2Annual - caM2N1) / caM2N1 * 100 : null;
    return {
      count: rows.length,
      avgSurf, avgSurfCount: surf.length, avgMoTtc,
      caM2: caM2Monthly,
      caM2Annual, caM2N1, caM2N2, caM2AnnualVsN1,
      avgN1Monthly: avgN1 != null ? avgN1 / 12 : null,
      avgN2Monthly: avgN2 != null ? avgN2 / 12 : null,
    };
  }, [filtered]);

  // Extrait l'année (20xx) d'une date FDC quel que soit le format (YYYY-MM-DD ou DD/MM/YYYY).
  const fdcYear = (c) => { const m = String(c && c.date_fdc || '').match(/(20\d{2})/); return m ? m[1] : null; };

  // Qualité — suit les filtres du tableau (sauf le filtre qualité lui-même).
  const qualityBase = useMemo(() => (filterQuality === ALL ? filtered : corners.filter(c => {
    const q = search.trim().toLowerCase();
    if (q && !((c.name_powerbi || '').toLowerCase().includes(q) || (c.name_sf || '').toLowerCase().includes(q))) return false;
    if (filterDr !== ALL && (c.nom_dr || '') !== filterDr) return false;
    if (filterBm !== ALL && (c.nom_bm || '') !== filterBm) return false;
    const t = c.trend;
    if (filterTrend === 'up') return t != null && t > 0;
    if (filterTrend === 'down') return t != null && t < 0;
    if (filterTrend === 'nc') return t == null;
    return true;
  })), [filtered, corners, search, filterDr, filterBm, filterTrend, filterQuality]);

  const qualityStats = useMemo(() => {
    const withQ = qualityBase.filter(c => c.quality_avg != null);
    const n = withQ.length;
    if (!n) return { avg: null, n: 0, green: 0, orange: 0, red: 0, pGreen: 0, pOrange: 0, pRed: 0 };
    const avg = withQ.reduce((s, c) => s + c.quality_avg, 0) / n;
    const green = withQ.filter(c => c.quality_avg >= 95).length;
    const orange = withQ.filter(c => c.quality_avg >= 90 && c.quality_avg < 95).length;
    const red = withQ.filter(c => c.quality_avg < 90).length;
    return { avg, n, green, orange, red, pGreen: green / n * 100, pOrange: orange / n * 100, pRed: red / n * 100 };
  }, [qualityBase]);

  // Évolution des zones qualité vs le mois précédent : on recalcule chaque moyenne sans les audits du dernier mois.
  const qualityTrend = useMemo(() => {
    const series = qualityBase.map(c => parseQualityHistory(c.quality_detail).map(a => ({ ...a, mi: monthIndex(a.month) })).filter(a => a.mi >= 0));
    const counts = {};
    series.forEach(sr => sr.forEach(a => { counts[a.mi] = (counts[a.mi] || 0) + 1; }));
    const audited = series.filter(sr => sr.length).length;
    const months = Object.keys(counts).map(Number).filter(mi => counts[mi] >= Math.max(3, audited * 0.05));
    if (!months.length) return null;
    const ref = Math.max(...months);
    const prev = { green: 0, orange: 0, red: 0 };
    series.forEach(sr => {
      const before = sr.filter(a => a.mi < ref);
      if (!before.length) return;
      prev[zoneOfValue(before.reduce((t, a) => t + a.value, 0) / before.length)] += 1;
    });
    return { prev, refLabel: MONTH_LABELS[ref], prevLabel: MONTH_LABELS[(ref + 11) % 12] };
  }, [qualityBase]);

  // Compteurs des échéances de contrat (Date FDC) par année, sur l'ensemble du parc.
  const fdcCounts = useMemo(() => {
    const acc = {};
    filtered.forEach(c => { const y = fdcYear(c); if (y) acc[y] = (acc[y] || 0) + 1; });
    return acc;
  }, [filtered]);

  // Correspondance d'un corner avec le surlignage FDC actif (année précise ou « 2029+ »).
  const fdcMatch = (c) => {
    if (!fdcHighlight.length) return false;
    const y = fdcYear(c);
    if (!y) return false;
    const n = parseInt(y, 10);
    return fdcHighlight.some(k => (k === '2029plus' ? n >= 2029 : k === 'avant2026' ? n < 2026 : y === k));
  };

  const qualityZoneOf = (c) => {
    const q = c.quality_avg;
    if (q == null) return null;
    return q >= 95 ? 'green' : q >= 90 ? 'orange' : 'red';
  };
  const QUALITY_ROW_HL = {
    green: 'bg-emerald-100 dark:bg-emerald-900/30', orange: 'bg-orange-100 dark:bg-orange-900/30', red: 'bg-red-100 dark:bg-red-900/30',
  };
  const QUALITY_ROW_RING = { green: 'ring-1 ring-inset ring-emerald-300', orange: 'ring-1 ring-inset ring-orange-300', red: 'ring-1 ring-inset ring-red-300' };
  useEffect(() => {
    if (!qualityHighlight) return;
    const first = filtered.find(c => qualityZoneOf(c) === qualityHighlight);
    const el = first && document.querySelector(`[data-testid="ca-corner-row-${first.id}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [qualityHighlight]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll automatique vers le 1er corner concerné quand on active un surlignage FDC.
  useEffect(() => {
    if (!fdcHighlight.length) return;
    const first = filtered.find(c => fdcMatch(c));
    if (first) {
      const el = document.querySelector(`[data-testid="ca-corner-row-${first.id}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [fdcHighlight]); // eslint-disable-line react-hooks/exhaustive-deps

  const cockpit = useMemo(() => {
    // Cockpit = vision PARC GLOBAL : on ignore volontairement les filtres du Tableau détaillé
    // (recherche corner, DR, Business Manager, tendance). Base = tous les corners.
    // Exclusion des magasins de l'Île de la Réunion (enseigne "Carrefour R") — France métropole uniquement.
    const isReunion = (c) => (c.enseigne || '').trim().toLowerCase() === 'carrefour r';
    const list = corners.filter(c => !isReunion(c));
    const withCA = list.filter(c => c.ca_n_current);
    const totalCA = withCA.reduce((s, c) => s + (c.ca_n_current || 0), 0);
    const trends = list.filter(c => c.trend !== null && c.trend !== undefined);
    const up = trends.filter(c => c.trend > 0).length;
    const down = trends.filter(c => c.trend < 0).length;
    const drMap = {};
    list.forEach(c => {
      const k = c.nom_dr || '— Non renseigné';
      if (!drMap[k]) drMap[k] = { dr: k, count: 0, ca: 0, trendSum: 0, trendCount: 0 };
      drMap[k].count++;
      drMap[k].ca += c.ca_n_current || 0;
      if (c.trend !== null && c.trend !== undefined) { drMap[k].trendSum += c.trend; drMap[k].trendCount++; }
    });
    const byDr = Object.values(drMap)
      .map(d => ({ ...d, trendAvg: d.trendCount ? d.trendSum / d.trendCount : null }))
      .sort((a, b) => b.ca - a.ca);
    const perM2 = list
      .filter(c => c.surface_m2 && c.ca_n_current)
      .map(c => ({ ...c, caM2: c.ca_n_current / c.surface_m2 }))
      .sort((a, b) => b.caM2 - a.caM2)
      .slice(0, 10);
    const topUp = [...trends].sort((a, b) => b.trend - a.trend).slice(0, 10);
    const topDown = [...trends].sort((a, b) => a.trend - b.trend).slice(0, 10);
    const topTtcN = withCA.slice()
      .sort((a, b) => (ttc(b.ca_n_current) || 0) - (ttc(a.ca_n_current) || 0)).slice(0, 10);
    const topTtcN1 = list.filter(c => c.ca_n1)
      .sort((a, b) => (b.ca_n1 || 0) - (a.ca_n1 || 0)).slice(0, 10);
    const topMom = list.filter(c => c.mom !== null && c.mom !== undefined)
      .sort((a, b) => b.mom - a.mom).slice(0, 10);
    const top3 = withCA.slice().sort((a, b) => (b.ca_n_current || 0) - (a.ca_n_current || 0)).slice(0, 3);
    return { count: list.length, totalCA, up, down, nc: list.length - trends.length, byDr, perM2, topUp, topDown, topTtcN, topTtcN1, topMom, top3 };
  }, [corners]);

  const zoneData = useMemo(() => {
    const groups = { NO: [], NE: [], SO: [], SE: [] };
    corners.forEach(c => { if (c.zone && groups[c.zone]) groups[c.zone].push(c); });
    ZONE_ORDER.forEach(z => groups[z].sort((a, b) => (b.ca_n_current || 0) - (a.ca_n_current || 0)));
    const dots = corners.filter(c => c.lat != null && c.lng != null && c.zone);
    const reunionDots = corners.filter(c => c.region === 'reunion' && c.lat != null && c.lng != null);
    const offmap = corners.filter(c => !c.zone).sort((a, b) => (b.ca_n_current || 0) - (a.ca_n_current || 0));
    return { groups, dots, reunionDots, ungeo: offmap.length, offmap };
  }, [corners]);
  const projX = (lng) => (lng - FRANCE_PROJ.lngMin) * FRANCE_PROJ.kx * FRANCE_PROJ.scale;
  const projY = (lat) => (FRANCE_PROJ.latMax - lat) * FRANCE_PROJ.scale;
  const mapSvgHtml = useMemo(() => {
    const esc = (s) => String(s || '').replace(/[<>&]/g, c => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c]));
    let s = '';
    for (const d of DEPARTEMENTS) {
      const z = d.sector;
      const active = z && selectedZone === z;
      const dim = selectedZone && z !== selectedZone;
      const fill = z ? ZONES[z].dot : '#e2e8f0';
      const op = active ? 0.85 : dim ? 0.22 : 0.5;
      const attrs = z ? `data-sector="${z}" style="cursor:pointer"` : 'style="pointer-events:none"';
      s += `<path d="${d.d}" fill="${fill}" fill-opacity="${op}" stroke="#ffffff" stroke-width="0.6" stroke-linejoin="round" ${attrs}><title>${d.code} · ${esc(d.nom)}${z ? ` — ${ZONES[z].label}` : ''}</title></path>`;
    }
    for (const c of zoneData.dots) {
      const sel = selectedZone === c.zone;
      const dim = selectedZone && !sel;
      s += `<circle cx="${projX(c.lng).toFixed(1)}" cy="${projY(c.lat).toFixed(1)}" r="${sel ? 5.5 : 4}" fill="#0f172a" opacity="${dim ? 0.15 : 0.9}" stroke="#ffffff" stroke-width="1.1" data-corner="${c.id}" style="cursor:pointer"><title>${esc(c.name_powerbi)} — ${esc(ZONES[c.zone]?.label)}</title></circle>`;
    }
    return s;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedZone, zoneData]);
  const openZone = (z) => { setSelectedZone(z); setZoneDialog(true); setView('table'); };

  const newClients = useMemo(() => {
    const now = new Date();
    const cutoff = new Date(now.getFullYear(), now.getMonth() - 6, 1);
    return corners
      .filter(c => { const d = parseFRDate(c.date_contrat); return d && d >= cutoff; })
      .sort((a, b) => (parseFRDate(b.date_contrat) - parseFRDate(a.date_contrat)));
  }, [corners]);

  const updateCornerField = async (id, patch) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/ca-corner/corners/${id}`, {
        method: 'PATCH', headers: { ...authHeaders, 'Content-Type': 'application/json' }, body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error();
      const updated = await res.json();
      setCorners(prev => prev.map(c => (c.id === id ? { ...c, ...updated } : c)));
      toast.success('Enregistré');
    } catch (e) { toast.error("Échec de l'enregistrement"); }
  };

  // Sauvegarde de la Date FDC (admin/super_admin) : ISO du champ date → format FR JJ/MM/AAAA.
  const fdcSavingRef = useRef(false);
  const saveFdc = async (id, isoVal) => {
    if (fdcSavingRef.current) return; // évite un double PATCH (Enter puis blur)
    fdcSavingRef.current = true;
    setFdcEditing(null);
    let fr = null;
    if (isoVal) {
      const m = String(isoVal).match(/^(\d{4})-(\d{2})-(\d{2})/);
      fr = m ? `${m[3]}/${m[2]}/${m[1]}` : isoVal;
    }
    try { await updateCornerField(id, { date_fdc: fr }); }
    finally { fdcSavingRef.current = false; }
  };

  // Nouveaux clients « En retard » : run-rate < -15 % vs prévi après >= 3 mois pleins.
  const lateNewClients = useMemo(() => newClients.filter(c => {
    const me = monthsElapsed(c);
    if (!me || me < 3 || !c.previ_ca_annuel_ht || c.ca_n_current == null) return false;
    const runRate = runRateAnnual(c);
    if (runRate == null) return false;
    return (runRate - c.previ_ca_annuel_ht) / c.previ_ca_annuel_ht * 100 <= -15;
  }), [newClients]);

  const [sendingAlert, setSendingAlert] = useState(false);
  const sendLateAlert = async () => {
    setSendingAlert(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/ca-corner/alerts/run`, { method: 'POST', headers: authHeaders });
      if (!res.ok) throw new Error();
      const d = await res.json();
      if (d.emails_sent) toast.success(`Alerte envoyée aux Super Admins — ${d.late_count} corner(s) en retard`);
      else toast.info(`Aucun email envoyé (${d.late_count} corner(s) en retard, envoi email peut-être désactivé)`);
      loadAlertLog();
    } catch (e) { toast.error("Échec de l'envoi de l'alerte"); }
    finally { setSendingAlert(false); }
  };

  // Corners sous le seuil qualité configuré sur l'ensemble du parc (indépendant des filtres).
  const lowQualityCorners = useMemo(
    () => corners.filter(c => c.quality_avg != null && c.quality_avg < qualityThreshold),
    [corners, qualityThreshold]);

  const sendQualityAlert = async () => {
    setSendingQualityAlert(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/ca-corner/quality-alerts/run`, { method: 'POST', headers: authHeaders });
      if (!res.ok) throw new Error();
      const d = await res.json();
      if (d.emails_sent) toast.success(`Alerte qualité envoyée — ${d.low_count} corner(s) sous 90 %`);
      else toast.info(`Aucun email envoyé (${d.low_count} corner(s) sous 90 % · destinataires non configurés ou envoi en pause)`);
    } catch (e) { toast.error("Échec de l'envoi de l'alerte qualité"); }
    finally { setSendingQualityAlert(false); }
  };

  const loadAlertLog = async () => {
    setAlertLogLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/ca-corner/alert-log`, { headers: authHeaders });
      if (res.ok) { const d = await res.json(); setAlertLog(d.logs || []); }
    } catch (e) { /* silencieux */ }
    finally { setAlertLogLoading(false); }
  };

  const loadRunrate = async (id) => {
    setRrCornerId(id);
    if (!id) { setRrData(null); return; }
    setRrLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/ca-corner/corners/${id}/runrate-history`, { headers: authHeaders });
      if (res.ok) setRrData(await res.json());
    } catch (e) { toast.error('Échec du chargement de la courbe'); }
    finally { setRrLoading(false); }
  };

  useEffect(() => { if (view === 'alert-history') loadAlertLog(); /* eslint-disable-next-line */ }, [view]);


  const TrendCell = ({ v }) => {
    if (v === null || v === undefined) return <span className="text-slate-400 font-medium">NC</span>;
    if (v > 0) return <span className="inline-flex items-center gap-1 text-emerald-600 font-semibold"><TrendingUp className="w-4 h-4" />+{v.toFixed(1)}%</span>;
    if (v < 0) return <span className="inline-flex items-center gap-1 text-red-600 font-semibold"><TrendingDown className="w-4 h-4" />{v.toFixed(1)}%</span>;
    return <span className="inline-flex items-center gap-1 text-slate-400 font-medium"><Minus className="w-4 h-4" />0%</span>;
  };

  const Th = ({ label, k, align = 'left', tip, sticky }) => (
    <th className={`px-3 py-3 text-${align} text-xs font-semibold uppercase tracking-wide text-slate-500 select-none whitespace-nowrap ${k ? 'cursor-pointer hover:text-slate-800' : ''} ${sticky ? 'sticky left-0 z-20 bg-slate-100 dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700' : ''}`}
        onClick={() => k && toggleSort(k)} data-testid={`ca-corner-sort-${k || label}`}>
      <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'justify-end w-full' : ''}`}>
        {label}
        {tip && (
          <UiTooltip>
            <TooltipTrigger asChild>
              <span onClick={(e) => e.stopPropagation()} className="inline-flex cursor-help text-slate-400 hover:text-indigo-600" data-testid={`ca-corner-tip-${k || label}`}>
                <Info className="w-3.5 h-3.5" />
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-[280px] text-xs leading-relaxed normal-case font-normal">{tip}</TooltipContent>
          </UiTooltip>
        )}
        {k && <ArrowUpDown className={`w-3 h-3 ${sortKey === k ? 'text-slate-800' : 'text-slate-300'}`} />}
      </span>
    </th>
  );

  const COLUMN_DEFS = [
    { key: 'marque', label: 'Marque', always: true, align: 'center', nowrap: true,
      tip: "Marque du corner (WASABI ou SORAE), issue de la colonne « Marque » du fichier importé. Le logo est déterminé automatiquement d'après ce nom.",
      cell: (c) => <BrandLogoCell marque={c.marque} />,
      hiddenCell: (c) => <BrandLogoCell marque={c.marque} /> },
    { key: 'name_powerbi', label: 'Corner (Power BI)', always: true, sortKey: 'name_powerbi', align: 'left',
      tip: "Nom du corner tel qu'il apparaît dans Power BI (référentiel officiel). Sert de clé de rapprochement lors des imports mensuels.",
      cell: (c) => {
        const t = tensionByCorner[c.id];
        return (
          <span className="inline-flex items-center gap-2">
            {(t?.active || canEditTension) && <button type="button" title={t?.active ? 'Corner en tension' : 'Mettre en tension'}
              onClick={(e) => { e.stopPropagation(); setTensionCorner(c); }} data-testid={`tension-flag-${c.id}`}
              className={`shrink-0 rounded p-0.5 transition-colors ${t?.active ? '' : 'text-slate-300 hover:text-orange-500'}`}>
              {t?.active ? <span className={`block w-2.5 h-2.5 rounded-full ${TENSION_LEVELS[t.level]?.dot}`} /> : <Flag className="w-3.5 h-3.5" />}
            </button>}
            <span className="font-semibold text-slate-800 dark:text-slate-100">{c.name_powerbi || '—'}</span>
            {t?.active && <TensionPill level={t.level} testid={`tension-pill-${c.id}`} />}
          </span>
        );
      },
      hiddenCell: (c) => <span className="inline-flex items-center gap-1.5 font-semibold"><EyeOff className="w-3.5 h-3.5" />{c.name_powerbi || '—'}</span> },
    { key: 'name_sf', label: 'Compte SF', sortKey: 'name_sf', align: 'left', sticky: true,
      tip: "Nom du compte associé dans Salesforce (enseigne + ville). Colonne figée : reste visible lors du défilement horizontal.",
      cell: (c) => <span className="text-slate-500">{c.name_sf || '—'}</span> },
    { key: 'mois', label: 'Mois', sortKey: 'ca_n_month', align: 'left', nowrap: true, tip: "Mois du CA importé : dernier mois clôturé (CA M-1).", cell: (c) => fmtText(c.ca_n_month_label) },
    { key: 'ca_ht', label: 'CA HT', sortKey: 'ca_ht_monthly', align: 'right', nowrap: true, mono: true,
      tip: "CA hors taxes du seul mois affiché.",
      cell: (c) => (c.ca_ht_monthly == null || c.ca_ht_monthly === 0) ? <span className="text-slate-300 dark:text-slate-600">—</span> : fmtEur(c.ca_ht_monthly) },
    { key: 'ca_ttc', label: 'CA Mois TTC', sortKey: 'ca_ht_monthly', align: 'right', nowrap: true, mono: true, accent: true, tip: "CA du mois affiché, TTC = CA HT + TVA 10 %.", cell: (c) => (c.ca_ht_monthly == null || c.ca_ht_monthly === 0) ? <span className="text-slate-300 dark:text-slate-600">—</span> : fmtEur(ttc(c.ca_ht_monthly)) },
    { key: 'cumul', label: 'CA HT CUMUL N', sortKey: 'ca_n_current', align: 'right', nowrap: true, mono: true, tip: "CA hors taxes cumulé de l'année en cours (N), de janvier jusqu'au mois affiché.", cell: (c) => fmtEur(c.ca_n_current) },
    { key: 'cumul_ttc', label: 'CA N. CUMUL TTC', sortKey: 'ca_n_current', align: 'right', nowrap: true, mono: true, accent: true, tip: "CA cumulé de l'année en cours (N) en TTC (HT + TVA 10 %), de janvier jusqu'au mois affiché.", cell: (c) => fmtEur(ttc(c.ca_n_current)) },
    { key: 'ca_n1', label: 'CA N-1 TTC', sortKey: 'ca_n1', align: 'right', nowrap: true, mono: true, tip: "CA TTC de l'année complète précédente (N-1, 12 mois). Valeur de référence figée lors du 1er import.", cell: (c) => fmtEur(c.ca_n1) },
    { key: 'ca_n2', label: 'CA N-2 TTC', sortKey: 'ca_n2', align: 'right', nowrap: true, mono: true, tip: "CA TTC de l'année complète il y a deux ans (N-2, 12 mois). Valeur de référence figée lors du 1er import.", cell: (c) => fmtEur(c.ca_n2) },
    { key: 'trend', label: 'Tendance vs N-1', sortKey: 'trend', align: 'right', nowrap: true, tip: "Évolution du rythme par rapport à l'an dernier : compare le CA cumulé N (converti en TTC) au prorata de N-1 sur la même période, soit N-1 × (mois écoulés / 12). « NC » si N-2 est absent (N-1 non considérée comme complète).", cell: (c) => <TrendCell v={c.trend} /> },
    { key: 'runrate', label: 'Run-rate annuel HT', sortKey: 'ca_n_current', align: 'right', nowrap: true, mono: true, accent: true, tip: "Projection du CA annuel HT (atterrissage) calculée au jour près : CA HT cumulé N ÷ nombre de jours écoulés depuis le départ, × nombre de jours de l'année. Départ = date d'ouverture (1er contrat) si ouvert cette année ; sinon 1er janvier (notamment si un CA N-1 existe → CA réputé généré depuis le 1er janvier).", cell: (c) => { const rr = runRateAnnual(c); return rr == null ? <span className="text-slate-300 dark:text-slate-600">—</span> : fmtEur(rr); } },
    { key: 'commercial', label: 'Nom du commercial', sortKey: 'commercial_name', align: 'left', nowrap: true, tip: "Commercial qui suit ce client (sert aux Points individuels : échéances à 8 mois). Modifiable par Admin/Super Admin.",
      cell: (c) => isAdmin ? (
        <div className="flex flex-col gap-0.5">
        <select value={c.commercial_user_id || ''} onClick={e => e.stopPropagation()} onChange={(e) => updateCornerField(c.id, { commercial_user_id: e.target.value || null })}
          className="max-w-[170px] rounded border border-slate-200 bg-white px-1.5 py-1 text-xs dark:bg-slate-800 dark:border-slate-700" data-testid={`ca-corner-commercial-${c.id}`}>
          <option value="">{!c.commercial_user_id && c.commercial_name ? c.commercial_name : '— Non rattaché —'}</option>
          {responsables.filter(u => ['commercial', 'commerciale'].includes(u.role) && !/test/i.test(u.email || '')).map(u => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
        </select>
        </div>
      ) : fmtText(c.commercial_name) },
    { key: 'dr', label: 'Nom du DR', sortKey: 'nom_dr', align: 'left', nowrap: true, tip: "Directeur Régional en charge du corner.", cell: (c) => fmtText(c.nom_dr) },
    { key: 'bm', label: 'Business Manager', sortKey: 'nom_bm', align: 'left', nowrap: true, tip: "Business Manager en charge du corner.", cell: (c) => fmtText(c.nom_bm) },
    { key: 'date', label: 'Date 1er contrat', sortKey: 'date_contrat', align: 'left', nowrap: true, tip: "Date de signature du premier contrat du corner.", cell: (c) => fmtText(c.date_contrat) },
    { key: 'date_fdc', label: 'Date FDC', sortKey: 'date_fdc', align: 'left', nowrap: true, tip: "Date FDC = date de fin de contrat du corner. Modifiable par Admin/Super Admin (crayon). Une date modifiée manuellement est conservée lors des imports Excel (prioritaire sur le fichier).",
      cell: (c) => {
        if (isAdmin && fdcEditing === c.id) {
          return (
            <input type="date" autoFocus defaultValue={toISODate(c.date_fdc)}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => { if (e.key === 'Enter') saveFdc(c.id, e.target.value); if (e.key === 'Escape') setFdcEditing(null); }}
              onBlur={(e) => saveFdc(c.id, e.target.value)}
              className="h-7 px-1.5 rounded border border-amber-300 bg-white dark:bg-slate-900 text-xs outline-none focus:ring-2 focus:ring-amber-300"
              data-testid={`ca-corner-fdc-input-${c.id}`} />
          );
        }
        return (
          <span className="inline-flex items-center gap-1.5">
            {fmtText(c.date_fdc)}
            {isFdcPast(c.date_fdc) && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 text-[10px] font-semibold whitespace-nowrap"
                title="Contrat échu — renouvellement urgent" data-testid={`ca-corner-fdc-urgent-${c.id}`}>
                <AlertTriangle className="w-3 h-3" /> Renouvellement urgent
              </span>
            )}
            {c.date_fdc_manual && (
              <span title="Date modifiée manuellement — conservée lors des imports" className="text-amber-500" data-testid={`ca-corner-fdc-manual-${c.id}`}>
                <Pencil className="w-3 h-3" />
              </span>
            )}
            {isAdmin && (
              <button type="button" onClick={(e) => { e.stopPropagation(); setFdcEditing(c.id); }}
                className="ml-0.5 p-0.5 rounded text-slate-400 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors"
                title="Modifier la Date FDC" data-no-tap-min data-testid={`ca-corner-fdc-edit-${c.id}`}>
                <Pencil className="w-3.5 h-3.5" />
              </button>
            )}
          </span>
        );
      } },
    { key: 'surface', label: 'Surface', sortKey: 'surface_m2', align: 'right', nowrap: true, mono: true, tip: "Surface du corner, en m².", cell: (c) => fmtSurface(c.surface_m2) },
    { key: 'ca_ttc_m2', label: 'CA TTC au m² Corner', sortKey: 'ca_ttc_m2', align: 'right', nowrap: true, mono: true, accent: true, tip: "Performance au m² : CA TTC du mois affiché ÷ surface du corner (€/m²).", cell: (c) => fmtEurM2(caTtcM2(c)) },
    { key: 'taux', label: 'Taux comm.', sortKey: 'taux_commission', align: 'right', nowrap: true, mono: true, tip: "Taux de commission appliqué au corner (%).", cell: (c) => fmtTaux(c.taux_commission) },
    { key: 'quality', label: 'Moyenne qualité', sortKey: 'quality_avg', align: 'center', nowrap: true,
      tip: "Moyenne des audits qualité du corner. Code couleur : vert ≥ 95 %, orange 90–94,99 %, rouge < 90 %. Survolez le badge pour voir le détail des audits (mois et scores).",
      cell: (c) => <QualityBadge value={c.quality_avg} detail={c.quality_detail} />,
      hiddenCell: (c) => <QualityBadge value={c.quality_avg} detail={c.quality_detail} /> },
    { key: 'foodcost', label: 'Achat (Foodcost)', sortKey: 'foodcost', align: 'center', nowrap: true,
      tip: "Ratio d'achat (Foodcost). Objectif : ≤ 24 %. Vert si ≤ 24 %, orange clair au-dessus.",
      cell: (c) => <RatioBadge value={c.foodcost} objective={RATIO_OBJECTIVES.foodcost} /> },
    { key: 'ratio_saumon', label: 'Ratio Saumon', sortKey: 'ratio_saumon', align: 'center', nowrap: true,
      tip: "Ratio Saumon. Objectif : ≤ 7,50 %. Vert si ≤ 7,50 %, orange clair au-dessus.",
      cell: (c) => <RatioBadge value={c.ratio_saumon} objective={RATIO_OBJECTIVES.ratio_saumon} digits={2} /> },
    { key: 'taux_casse', label: 'Taux de casse', sortKey: 'taux_casse', align: 'center', nowrap: true,
      tip: "Taux de casse. Objectif : ≤ 10 %. Vert si ≤ 10 %, orange clair au-dessus.",
      cell: (c) => <RatioBadge value={c.taux_casse} objective={RATIO_OBJECTIVES.taux_casse} /> },
    { key: 'rendement_saumon', label: 'Rendement saumon', sortKey: 'rendement_saumon', align: 'center', nowrap: true,
      tip: "Rendement saumon moyen (€/kg). Objectif : ≥ 230 €/kg. Vert si ≥ 230, orange en dessous.",
      cell: (c) => <RatioBadge value={c.rendement_saumon} objective={230} higherIsBetter suffix=" €/kg" digits={0} /> },
    { key: 'productivite', label: 'Productivité h.', sortKey: 'productivite', align: 'center', nowrap: true,
      tip: "Productivité horaire moyenne (€/heure travaillée). Objectif : ≥ 60 €/h. Vert si ≥ 60, orange en dessous.",
      cell: (c) => <RatioBadge value={c.productivite} objective={60} higherIsBetter suffix=" €/h" digits={1} /> },
  ];
  const isColVisible = (key) => visibleCols[key] !== false;
  const activeCols = COLUMN_DEFS.filter(col => col.always || isColVisible(col.key));
  const tdClass = (col, hidden) =>
    `px-3 py-3 ${col.align === 'right' ? 'text-right' : ''} ${col.mono ? 'font-mono tabular-nums' : ''} ${col.nowrap ? 'whitespace-nowrap' : ''} ${hidden ? 'text-slate-400' : (col.accent ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-600 dark:text-slate-300')}`;

  const InfoTip = ({ text }) => (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" onClick={(e) => e.stopPropagation()}
                className="inline-flex align-middle text-slate-400 hover:text-indigo-600 transition-colors cursor-help"
                data-testid="ca-corner-infotip" aria-label="Informations">
          <Info className="w-3.5 h-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 text-xs leading-relaxed text-slate-600 dark:text-slate-300" align="start" onClick={(e) => e.stopPropagation()}>
        {text}
      </PopoverContent>
    </Popover>
  );

  const Stat = ({ icon: Icon, label, value, color, info }) => (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 p-4 bg-white dark:bg-slate-900">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-400 mb-2">
        <Icon className={`w-4 h-4 text-${color}-500`} /> {label} {info && <InfoTip text={info} />}
      </div>
      <p className="text-2xl font-bold text-slate-900 dark:text-white tabular-nums">{value}</p>
    </div>
  );

  const RankList = ({ title, items, render, empty, testid, info }) => (
    <Card className="border-slate-200 dark:border-slate-800">
      <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-1.5">{title} {info && <InfoTip text={info} />}</CardTitle></CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center">{empty}</p>
        ) : (
          <div className="divide-y divide-slate-100 dark:divide-slate-800" data-testid={testid}>
            {items.map((it, i) => (
              <button key={it.id || i} onClick={() => it.id && handleRowClick(it.id)}
                      className="w-full flex items-center gap-3 px-1 py-2.5 text-left hover:bg-indigo-50/60 dark:hover:bg-indigo-900/20 rounded transition-colors">
                <span className={`w-6 h-6 shrink-0 rounded-full text-xs font-bold flex items-center justify-center ${i < 3 ? 'bg-indigo-600 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-500'}`}>{i + 1}</span>
                {render(it)}
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );

  // Rappel : le CA du mois précédent (M-1) n'a pas encore été importé
  const today = new Date();
  const expected = { m: today.getMonth() === 0 ? 12 : today.getMonth(), y: today.getMonth() === 0 ? today.getFullYear() - 1 : today.getFullYear() };
  const importMissing = isAdmin && !loading && (!lastImport || (lastImport.year * 12 + lastImport.month) < (expected.y * 12 + expected.m));
  const expectedLabel = `${new Date(expected.y, expected.m - 1, 1).toLocaleDateString('fr-FR', { month: 'long' })} ${expected.y}`;

  return (
    <AppLayout>
    <div className="max-w-[2200px] mx-auto space-y-6" data-testid="ca-corner-page">
      <PageHeader overline="Pilotage · Performance commerciale par corner" title="CA CORNER">
        {isAdmin && (
          <Button onClick={() => setShowImport(v => !v)} data-testid="ca-corner-import-toggle"
            title={importMissing ? `CA de ${expectedLabel} pas encore importé` : undefined}
            className={`relative gap-2 font-semibold ${showImport ? 'bg-graphite-900 hover:bg-graphite-800 text-white' : 'bg-orange-500 hover:bg-orange-600 text-white'}`}>
            {showImport ? <X className="w-4 h-4" /> : <Upload className="w-4 h-4" />} {showImport ? "Fermer l'import" : 'IMPORT'}
            {importMissing && (
              <span className="absolute -top-2 -right-2 flex h-5 w-5" data-testid="ca-corner-import-reminder">
                <span className="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-60 animate-ping" />
                <span className="relative inline-flex h-5 w-5 rounded-full bg-red-600 text-white text-[11px] font-bold items-center justify-center ring-2 ring-white">!</span>
              </span>
            )}
          </Button>
        )}
        {importMissing && !showImport && (
          <span className="text-xs text-red-600 font-medium" data-testid="ca-corner-import-reminder-text">CA de {expectedLabel} à importer</span>
        )}
      </PageHeader>

      {/* Zone d'import — Admin / Super Admin uniquement, ouverte via le bouton IMPORT */}
      {isAdmin && showImport && (
      <Card className="border-slate-200 dark:border-slate-800">
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg"><Upload className="w-5 h-5" /> Import mensuel du CA</CardTitle>
              <CardDescription>
                {lastImport
                  ? `Dernier import : ${lastImport.month_label} ${lastImport.year} — le ${(lastImport.validated_at || '').slice(0, 10)} par ${lastImport.uploaded_by_name || lastImport.uploaded_by}`
                  : 'Aucun import validé pour le moment'}
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={openHistory} className="gap-2" data-testid="ca-corner-history-btn">
              <History className="w-4 h-4" /> Historique des imports
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <Label className="mb-1.5 block">Fichier Excel (.xlsx)</Label>
              <Input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={(e) => { setSelectedFile(e.target.files[0]); setPreview(null); }} data-testid="ca-corner-file-input" />
            </div>
            <div>
              <Label className="mb-1.5 block">Mois concerné (CA M-1)</Label>
              <select value={month} onChange={(e) => setMonth(Number(e.target.value))}
                      className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm dark:bg-slate-800 dark:border-slate-700" data-testid="ca-corner-month">
                {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
              </select>
            </div>
            <div>
              <Label className="mb-1.5 block">Année</Label>
              <Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="ca-corner-year" />
            </div>
          </div>

          {!preview ? (
            <Button onClick={handleAnalyze} disabled={analyzing || !selectedFile} className="gap-2" data-testid="ca-corner-analyze-btn">
              {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />} Analyser le fichier
            </Button>
          ) : (
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4 space-y-3 bg-slate-50 dark:bg-slate-800/50" data-testid="ca-corner-preview">
              <p className="font-semibold">Contrôles avant validation — {preview.month_label} {preview.year}</p>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                <div className="flex items-center gap-2"><FileSpreadsheet className="w-4 h-4 text-slate-500" /> {preview.rows_total} ligne(s) OK</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> {preview.corners_matched} reconnu(s)</div>
                <div className="flex items-center gap-2"><XCircle className="w-4 h-4 text-amber-600" /> {preview.corners_unknown_count} inconnu(s)</div>
                <div className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-500" /> {preview.duplicates_count} doublon(s)</div>
                <div className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-600" /> {preview.rejected_count || 0} non importable(s)</div>
                {preview.same_city_conflicts_count > 0 && <div className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-orange-500" /> {preview.same_city_conflicts_count} ville(s) à confirmer</div>}
              </div>
              {preview.is_first_import && <p className="text-xs text-indigo-600">Premier import : les colonnes de référence CA N-1 / N-2 seront enregistrées.</p>}
              {preview.corners_unknown_count > 0 && (
                <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-900/20 p-3" data-testid="ca-corner-unknown-list">
                  <p className="text-sm font-semibold text-amber-800 dark:text-amber-300 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" /> {preview.corners_unknown_count} corner(s) non reconnu(s) — faut-il les créer ?
                  </p>
                  <p className="text-xs text-amber-700 dark:text-amber-400 mb-3">Activé = le corner sera créé. Désactivez pour ignorer une ligne non reconnue.</p>
                  <div className="max-h-56 overflow-y-auto space-y-1.5">
                    {preview.corners_unknown.map((name, i) => {
                      const willCreate = !unknownSkip.has(name);
                      return (
                        <div key={i} className="flex items-center justify-between gap-3 bg-white dark:bg-slate-800 rounded px-3 py-2 border border-amber-200 dark:border-amber-800">
                          <span className="text-sm font-medium truncate">{name}</span>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className={`text-xs font-medium ${willCreate ? 'text-emerald-600' : 'text-slate-400'}`}>{willCreate ? 'Créer' : 'Ignorer'}</span>
                            <Switch checked={willCreate} onCheckedChange={() => toggleUnknown(name)} data-testid={`ca-corner-unknown-toggle-${i}`} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {preview.same_city_conflicts_count > 0 && (
                <div className="rounded-md border border-orange-300 bg-orange-50 dark:bg-orange-900/20 p-3" data-testid="ca-corner-samecity-list">
                  <p className="text-sm font-semibold text-orange-800 dark:text-orange-300 mb-1 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" /> {preview.same_city_conflicts_count} ville(s) avec plusieurs enseignes — à confirmer
                  </p>
                  <p className="text-xs text-orange-700 dark:text-orange-400 mb-2">Ces magasins partagent la même ville mais ont des enseignes différentes : ils seront bien traités comme des corners distincts. Vérifiez qu'aucune ligne ne manque.</p>
                  <div className="max-h-56 overflow-y-auto space-y-2">
                    {preview.same_city_conflicts.map((g, i) => (
                      <div key={i} className="bg-white dark:bg-slate-800 rounded px-3 py-2 border border-orange-200 dark:border-orange-800" data-testid={`ca-corner-samecity-${i}`}>
                        <div className="text-xs font-bold text-orange-700 dark:text-orange-300 mb-1">{g.city}</div>
                        <div className="flex flex-wrap gap-1.5">
                          {g.corners.map((c, j) => (
                            <span key={j} className="inline-flex items-center gap-1 text-xs bg-orange-100 dark:bg-orange-800/40 text-orange-800 dark:text-orange-200 rounded-full px-2 py-0.5">
                              {c.name}{c.brand ? <span className="opacity-60">· {c.brand}</span> : null}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {preview.rejected_count > 0 && (
                <div className="rounded-md border border-red-300 bg-red-50 dark:bg-red-900/20 p-3" data-testid="ca-corner-rejected-list">
                  <p className="text-sm font-semibold text-red-800 dark:text-red-300 mb-1 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" /> {preview.rejected_count} ligne(s) non importable(s)
                  </p>
                  <p className="text-xs text-red-700 dark:text-red-400 mb-2">Ces lignes du fichier ne peuvent pas être mises à jour — détail et raison ci-dessous.</p>
                  <div className="max-h-56 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead><tr className="text-left text-red-700/70 dark:text-red-400/70"><th className="py-1 pr-3">Ligne</th><th className="py-1 pr-3">Corner</th><th className="py-1">Raison</th></tr></thead>
                      <tbody>
                        {preview.rejected_rows.map((r, i) => (
                          <tr key={i} className="border-t border-red-200/60 dark:border-red-800/60">
                            <td className="py-1 pr-3 font-mono">{r.row ?? '—'}</td>
                            <td className="py-1 pr-3 font-medium">{r.name || <span className="italic text-red-400">nom absent</span>}</td>
                            <td className="py-1 text-red-700 dark:text-red-300">{r.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              <div className="flex gap-3 pt-1">
                <Button onClick={handleValidate} disabled={validating} className="gap-2 bg-emerald-600 hover:bg-emerald-700" data-testid="ca-corner-validate-btn">
                  {validating ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Valider l'import
                </Button>
                <Button onClick={handleCancelPreview} variant="outline" data-testid="ca-corner-cancel-btn">Annuler / Remplacer</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {isAdmin && showImport && lastReport && (
        <Card className="border-red-300 dark:border-red-800" data-testid="ca-corner-import-report">
          <CardHeader className="pb-2">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <CardTitle className="text-base flex items-center gap-2 text-red-700 dark:text-red-300"><AlertTriangle className="w-4 h-4" /> Rapport d'import — {lastReport.not_imported_count} ligne(s) non importée(s)</CardTitle>
                <CardDescription>{lastReport.updated} mis à jour · {lastReport.created} créé(s) · {lastReport.skipped} ignoré(s) — {lastReport.month_label} {lastReport.year}</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={downloadReportCsv} className="gap-1" data-testid="ca-corner-report-csv"><FileSpreadsheet className="w-4 h-4 text-emerald-600" /> Télécharger (CSV)</Button>
                <Button variant="ghost" size="sm" onClick={() => setLastReport(null)} data-testid="ca-corner-report-close">Fermer</Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="max-h-72 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase text-slate-400"><tr><th className="py-1.5 pr-3">Ligne</th><th className="py-1.5 pr-3">Corner</th><th className="py-1.5">Raison</th></tr></thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800" data-testid="ca-corner-report-rows">
                  {lastReport.not_imported.map((r, i) => (
                    <tr key={i}>
                      <td className="py-1.5 pr-3 font-mono text-slate-500">{r.row ?? '—'}</td>
                      <td className="py-1.5 pr-3 font-medium">{r.name || <span className="italic text-slate-400">nom absent</span>}</td>
                      <td className="py-1.5 text-slate-600 dark:text-slate-300">{r.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <TensionDialog corner={tensionCorner} tension={tensionCorner ? tensionByCorner[tensionCorner.id] : null} motifs={tensionMotifs}
        token={token} canEdit={canEditTension} onClose={() => setTensionCorner(null)}
        onSaved={() => { setTensionCorner(null); loadTensions(); }} />
      <Tabs value={view} onValueChange={setView} className="w-full">
        <TabsList data-testid="ca-corner-tabs" className="inline-flex flex-wrap gap-1 rounded-full bg-slate-100 dark:bg-slate-800 p-1 h-auto">
          <TabsTrigger value="table" data-testid="ca-corner-tab-table" className="gap-1.5 rounded-full px-4 py-1.5 text-sm data-[state=active]:bg-orange-500 data-[state=active]:text-white data-[state=active]:shadow-sm text-slate-600 dark:text-slate-300"><TableIcon className="w-4 h-4" /> Tableau détaillé</TabsTrigger>
          <TabsTrigger value="cockpit" data-testid="ca-corner-tab-cockpit" className="gap-1.5 rounded-full px-4 py-1.5 text-sm data-[state=active]:bg-orange-500 data-[state=active]:text-white data-[state=active]:shadow-sm text-slate-600 dark:text-slate-300"><LayoutGrid className="w-4 h-4" /> Cockpit commercial</TabsTrigger>
          <TabsTrigger value="facturation" data-testid="ca-corner-tab-facturation" className="gap-1.5 rounded-full px-4 py-1.5 text-sm data-[state=active]:bg-orange-500 data-[state=active]:text-white data-[state=active]:shadow-sm text-slate-600 dark:text-slate-300"><FileText className="w-4 h-4" /> Suivi Facturation</TabsTrigger>
          <TabsTrigger value="parc" data-testid="ca-corner-tab-parc" className="gap-1.5 rounded-full px-4 py-1.5 text-sm data-[state=active]:bg-orange-500 data-[state=active]:text-white data-[state=active]:shadow-sm text-slate-600 dark:text-slate-300"><TrendingDown className="w-4 h-4" /> Analyse du parc</TabsTrigger>
          <TabsTrigger value="perf" data-testid="ca-corner-tab-perf" className="gap-1.5 rounded-full px-4 py-1.5 text-sm data-[state=active]:bg-orange-500 data-[state=active]:text-white data-[state=active]:shadow-sm text-slate-600 dark:text-slate-300"><Scale className="w-4 h-4" /> Analyse performance</TabsTrigger>
          <TabsTrigger value="tensions" data-testid="ca-corner-tab-tensions" className="gap-1.5 rounded-full px-4 py-1.5 text-sm data-[state=active]:bg-orange-500 data-[state=active]:text-white data-[state=active]:shadow-sm text-slate-600 dark:text-slate-300"><Flag className="w-4 h-4" /> Corners en tension{activeTensionCount > 0 ? ` (${activeTensionCount})` : ''}</TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="alert-history" data-testid="ca-corner-tab-alert-history" className="gap-1.5 rounded-full px-4 py-1.5 text-sm data-[state=active]:bg-orange-500 data-[state=active]:text-white data-[state=active]:shadow-sm text-slate-600 dark:text-slate-300"><History className="w-4 h-4" /> Historique alertes</TabsTrigger>
          )}
        </TabsList>

        {/* Vue Tableau */}
        <TabsContent value="table" className="space-y-4">
          {/* Carte des corners par zone France */}
          <Card className="border-slate-200 dark:border-slate-800" data-testid="ca-corner-zone-map">
            <CardHeader className="pb-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-indigo-600" /> Carte des corners par secteur commercial
                  <InfoTip text="La France est découpée en 4 secteurs commerciaux par département (voir la répartition officielle). Chaque corner est rattaché à son secteur d'après son département (déduit de sa ville géocodée) et positionné par un point. Cliquez sur un secteur pour surligner ses corners dans le tableau. Les corners hors métropole (La Réunion) ne figurent pas sur la carte." />
                </CardTitle>
                {selectedZone && (
                  <Button variant="ghost" size="sm" onClick={() => setSelectedZone(null)} className="gap-1 text-slate-500" data-testid="ca-corner-zone-clear">
                    <X className="w-4 h-4" /> Effacer la sélection ({ZONES[selectedZone].label})
                  </Button>
                )}
                {isAdmin && (
                  <Button variant="outline" size="sm" onClick={handleGeocode} disabled={geocoding} className="gap-1.5" data-testid="ca-corner-geocode-btn">
                    {geocoding ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />} Actualiser la carte
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-[1.35fr_1fr] gap-6 items-center">
                <div className="relative mx-auto w-full max-w-[520px]">
                  <svg viewBox={`0 0 ${FRANCE_PROJ.W} ${FRANCE_PROJ.H}`} className="w-full h-auto" data-testid="ca-corner-map-svg"
                    onClick={(e) => {
                      const t = e.target;
                      const sec = t && t.getAttribute && t.getAttribute('data-sector');
                      if (sec) { openZone(sec); return; }
                      const cid = t && t.getAttribute && t.getAttribute('data-corner');
                      if (cid) handleRowClick(cid);
                    }}
                    dangerouslySetInnerHTML={{ __html: mapSvgHtml }} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {ZONE_ORDER.map(z => (
                    <button key={z} onClick={() => openZone(z)} data-testid={`ca-corner-zone-btn-${z}`}
                      className="rounded-xl border border-slate-200 dark:border-slate-800 p-4 text-left transition-all hover:shadow-sm"
                      style={selectedZone === z ? { borderColor: ZONES[z].color, boxShadow: `0 0 0 2px ${ZONES[z].color}44` } : {}}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="w-3 h-3 rounded-sm" style={{ background: ZONES[z].dot }} />
                        <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">Secteur {ZONES[z].label}</span>
                      </div>
                      <p className="text-2xl font-bold tabular-nums" style={{ color: ZONES[z].color }}>{zoneData.groups[z].length}</p>
                      <p className="text-xs text-slate-400">corners · {fmtEur(zoneData.groups[z].reduce((s, c) => s + (c.ca_n_current || 0), 0))}</p>
                    </button>
                  ))}
                  {zoneData.ungeo > 0 && (
                    <p className="col-span-2 text-xs text-slate-400">{zoneData.ungeo} corner(s) hors métropole ou non localisé(s), listés sous la carte.</p>
                  )}
                </div>
              </div>
              {zoneData.offmap.length > 0 && (
                <div className="mt-5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/40 p-4" data-testid="ca-corner-offmap">
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">La Réunion (hors métropole)</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 font-medium">{zoneData.offmap.length} corner(s)</span>
                    <span className="text-xs text-slate-500">CA cumulé&nbsp;: <span className="font-semibold text-slate-700 dark:text-slate-200">{fmtEur(zoneData.offmap.reduce((s, c) => s + (c.ca_n_current || 0), 0))}</span></span>
                  </div>
                  {zoneData.reunionDots.length > 0 && (
                    <div className="flex justify-center mb-4" data-testid="ca-corner-reunion-map">
                      <svg viewBox="0 0 240 180" className="w-full max-w-[340px] h-auto">
                        <defs>
                          <radialGradient id="reunionSea" cx="50%" cy="45%" r="70%">
                            <stop offset="0%" stopColor="#bae6fd" />
                            <stop offset="100%" stopColor="#7dd3fc" />
                          </radialGradient>
                        </defs>
                        <rect x="0" y="0" width="240" height="180" rx="14" fill="url(#reunionSea)" opacity="0.35" />
                        {/* Silhouette approximative de l'île */}
                        <ellipse cx="120" cy="92" rx="96" ry="72" fill="#86efac" fillOpacity="0.55" stroke="#22c55e" strokeWidth="1.4" />
                        <ellipse cx="118" cy="86" rx="34" ry="26" fill="#16a34a" fillOpacity="0.35" />
                        {zoneData.reunionDots.map(c => {
                          const px = 22 + ((c.lng - 55.18) / (55.86 - 55.18)) * 196;
                          const py = 14 + ((-20.85 - c.lat) / (-20.85 - (-21.42))) * 152;
                          const cx = Math.max(10, Math.min(230, px));
                          const cy = Math.max(10, Math.min(170, py));
                          return (
                            <circle key={c.id} cx={cx.toFixed(1)} cy={cy.toFixed(1)} r="5.5"
                              fill="#ea580c" stroke="#ffffff" strokeWidth="1.5"
                              className="cursor-pointer transition-opacity hover:opacity-75" onClick={() => handleRowClick(c.id)}
                              data-testid={`ca-corner-reunion-dot-${c.id}`}>
                              <title>{`${c.name_powerbi} — ${fmtEur(c.ca_n_current)}`}</title>
                            </circle>
                          );
                        })}
                      </svg>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-1.5">
                    {zoneData.offmap.map(c => (
                      <button key={c.id} onClick={() => handleRowClick(c.id)}
                        className="text-xs px-2.5 py-1 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-indigo-400 transition-colors"
                        data-testid={`ca-corner-offmap-${c.id}`}>
                        <span className="font-medium">{c.name_powerbi}</span>
                        <span className="text-slate-400"> · {fmtEur(c.ca_n_current)}</span>
                      </button>
                    ))}
                  </div>
                  <p className="text-[11px] text-slate-400 mt-2">Ces corners restent comptés dans le tableau et le cockpit, mais ne sont pas placés sur la carte de France métropolitaine.</p>
                </div>
              )}
            </CardContent>
          </Card>

          {!loading && qualityStats.n > 0 && (
            <QualityBrick stats={qualityStats} trend={qualityTrend} active={qualityHighlight} onPick={(z) => setQualityHighlight(qualityHighlight === z ? null : z)}>
              {isAdmin && lowQualityCorners.length > 0 && (
                <Button size="sm" variant="outline" onClick={sendQualityAlert} disabled={sendingQualityAlert}
                  className="shrink-0 border-red-300 text-red-700 hover:bg-red-100 dark:border-red-800 dark:text-red-300"
                  data-testid="ca-corner-send-quality-alert-btn">
                  {sendingQualityAlert ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  <span className="ml-1.5">Alerter les responsables</span>
                </Button>
              )}
            </QualityBrick>
          )}

          {!loading && filtered.length > 0 && (
            <TooltipProvider delayDuration={100}>
              <Card className="border-slate-200 dark:border-slate-800 mb-3" data-testid="ca-corner-park-averages">
                <CardContent className="py-4">
                  <button type="button" onClick={() => setShowKpi(v => !v)}
                    className="w-full flex items-center justify-between gap-2 text-left group"
                    data-testid="ca-corner-kpi-toggle">
                    <span className="text-sm font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-1.5">
                      <Euro className="w-4 h-4 text-indigo-600" /> Indicateurs du parc
                      <span className="text-xs font-normal text-slate-400">· {parkAverages.count} corner(s){qualityStats.n > 0 ? ` · qualité ${fmtQuality(qualityStats.avg)}` : ''}{parkAverages.caM2 != null ? ` · ${fmtEurM2(parkAverages.caM2)}/m²` : ''}</span>
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs text-indigo-600 dark:text-indigo-400 group-hover:underline shrink-0">
                      {showKpi ? (<><ChevronUp className="w-4 h-4" /> Réduire</>) : (<><ChevronDown className="w-4 h-4" /> Afficher les indicateurs</>)}
                    </span>
                  </button>
                  {showKpi && (<div className="mt-4">
                  <p className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3 flex items-center gap-1.5">
                    <Euro className="w-4 h-4 text-indigo-600" /> Moyennes du parc
                    <InfoTip text="Moyennes calculées sur les corners actuellement affichés (selon vos filtres). Le CA mensuel est exprimé en TTC (CA HT + TVA 10 %)." />
                    <span className="text-xs font-normal text-slate-400">· {parkAverages.count} corner(s)</span>
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <StatBox label="Surface moyenne" value={fmtSurface(parkAverages.avgSurf)} tip="Surface moyenne de nos corners (m²), sur la sélection affichée." />
                    <StatBox label="CA mensuel moyen TTC" valueClass="text-indigo-600 dark:text-indigo-400" value={fmtEur(parkAverages.avgMoTtc)} tip="Moyenne du CA du mois affiché (TTC) sur nos corners, pour la sélection affichée." />
                    <StatBox label="CA/m² mensuel (moy.)" valueClass="text-indigo-600 dark:text-indigo-400" value={fmtEurM2(parkAverages.caM2)} tip="CA au m² MENSUEL : CA TTC total du mois affiché ÷ surface totale des corners affichés (€/m²/mois)." />
                    <StatBox
                      label="Moy. mensuelle N-1"
                      value={<span>{fmtEur(parkAverages.avgN1Monthly)}{(parkAverages.avgMoTtc != null && parkAverages.avgN1Monthly) ? (() => { const d = (parkAverages.avgMoTtc - parkAverages.avgN1Monthly) / parkAverages.avgN1Monthly * 100; return <span className={`ml-1.5 text-xs ${d >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{d >= 0 ? '+' : ''}{d.toFixed(1)}%</span>; })() : null}</span>}
                      tip="Moyenne du CA annuel N-1 ramenée au mois (÷ 12), avec l'écart du CA mensuel moyen actuel vs cette référence." />
                    <StatBox label="Moy. mensuelle N-2" value={fmtEur(parkAverages.avgN2Monthly)} tip="Moyenne du CA annuel N-2 ramenée au mois (÷ 12), pour comparaison." />
                  </div>

                  {parkAverages.caM2 != null && (
                    <>
                      <p className="text-sm font-semibold text-slate-700 dark:text-slate-200 mt-5 mb-3 flex items-center gap-1.5">
                        <Euro className="w-4 h-4 text-indigo-600" /> CA au m² du parc
                        <InfoTip text="Moyenne des CA au m² par corner (robuste aux surfaces atypiques). « Mensuel » = CA TTC du mois ÷ surface, moyenné par corner. « Annuel (proj.) » = mensuel × 12. N-1 / N-2 = CA annuel TTC réel ÷ surface, moyenné par corner. L'écart compare la projection annuelle au réel N-1." />
                      </p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <StatBox label="CA/m² mensuel" valueClass="text-indigo-600 dark:text-indigo-400" value={fmtEurM2(parkAverages.caM2)} tip="CA TTC du mois affiché ÷ surface totale (€/m²/mois)." />
                        <StatBox
                          label="CA/m² annuel (proj.)"
                          valueClass="text-indigo-600 dark:text-indigo-400"
                          value={<span>{fmtEurM2(parkAverages.caM2Annual)}{parkAverages.caM2AnnualVsN1 != null ? (<span className={`ml-1.5 text-xs ${parkAverages.caM2AnnualVsN1 >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{parkAverages.caM2AnnualVsN1 >= 0 ? '+' : ''}{parkAverages.caM2AnnualVsN1.toFixed(1)}%</span>) : null}</span>}
                          tip="Projection annuelle = CA/m² mensuel × 12. L'écart affiché compare cette projection au CA/m² annuel N-1." />
                        <StatBox label="CA/m² annuel N-1" value={fmtEurM2(parkAverages.caM2N1)} tip="CA annuel TTC N-1 ÷ surface totale des corners renseignés (€/m²/an)." />
                        <StatBox label="CA/m² annuel N-2" value={fmtEurM2(parkAverages.caM2N2)} tip="CA annuel TTC N-2 ÷ surface totale des corners renseignés (€/m²/an)." />
                      </div>
                    </>
                  )}

                  {qualityStats.n > 0 && (
                    <>
                      <p className="text-sm font-semibold text-slate-700 dark:text-slate-200 mt-5 mb-3 flex items-center gap-1.5">
                        <ShieldCheck className="w-4 h-4 text-emerald-600" /> Qualité du parc
                        <InfoTip text="Note moyenne des audits qualité et répartition par zone, calculées sur l'ensemble des corners audités du parc (indépendant des filtres)." />
                        <span className="text-xs font-normal text-slate-400">· {qualityStats.n} corner(s) audité(s)</span>
                      </p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <StatBox label="Note moyenne N qualité"
                          value={<span className="inline-flex items-center gap-2">{fmtQuality(qualityStats.avg)}<QualityBadge value={qualityStats.avg} /></span>}
                          tip="Moyenne des notes d'audits qualité sur l'ensemble des corners audités du parc." />
                        <StatBox label="% zone verte (≥ 95 %)"
                          valueClass="text-emerald-600 dark:text-emerald-400"
                          value={`${qualityStats.pGreen.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %`}
                          tip={`${qualityStats.green} corner(s) avec une moyenne qualité supérieure ou égale à 95 %.`} />
                        <StatBox label="% zone orange (90–94,99 %)"
                          valueClass="text-orange-600 dark:text-orange-400"
                          value={`${qualityStats.pOrange.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %`}
                          tip={`${qualityStats.orange} corner(s) avec une moyenne qualité entre 90 % et 94,99 %.`} />
                        <StatBox label="% zone rouge (< 90 %)"
                          valueClass="text-red-600 dark:text-red-400"
                          value={`${qualityStats.pRed.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %`}
                          tip={`${qualityStats.red} corner(s) avec une moyenne qualité inférieure à 90 %.`} />
                      </div>
                    </>
                  )}

                  </div>)}

                  {(['2026', '2027', '2028'].some(y => (fdcCounts[y] || 0) > 0) || Object.keys(fdcCounts).some(y => parseInt(y, 10) < 2026)) && (
                    <>
                      <p className="text-sm font-semibold text-slate-700 dark:text-slate-200 mt-5 mb-3 flex items-center gap-1.5">
                        <CalendarClock className="w-4 h-4 text-indigo-600" /> Échéances de contrat (Date FDC)
                        <InfoTip text="Nombre de corners dont le contrat arrive à échéance (Date FDC) sur l'année. Cliquez sur une ou plusieurs années pour surligner les corners concernés dans le tableau ci-dessous. « Avant 2026 » regroupe les contrats déjà échus, « 2029+ » regroupe toutes les échéances à partir de 2029, et le champ « année » permet de cibler n'importe quelle année." />
                      </p>
                      <div className="flex flex-wrap items-center gap-3">
                        {(() => {
                          const active = fdcHighlight.includes('avant2026');
                          const count = Object.entries(fdcCounts).reduce((s, [yy, n]) => (parseInt(yy, 10) < 2026 ? s + n : s), 0);
                          if (count === 0) return null;
                          return (
                            <button type="button"
                              onClick={() => toggleFdc('avant2026')}
                              className={`group flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-all ${active
                                ? 'border-rose-400 bg-rose-50 dark:bg-rose-900/30 ring-2 ring-rose-300'
                                : 'border-slate-200 dark:border-slate-700 hover:border-rose-300 hover:bg-rose-50/50 dark:hover:bg-rose-900/10'}`}
                              data-testid="ca-corner-fdc-avant2026">
                              <span className={`flex items-center justify-center w-10 h-10 rounded-lg text-lg font-bold tabular-nums ${active ? 'bg-rose-500 text-white' : 'bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300'}`}>{count}</span>
                              <span>
                                <span className="block text-xs uppercase text-slate-400">Date FDC</span>
                                <span className="block text-sm font-semibold text-rose-600 dark:text-rose-300">Avant 2026</span>
                              </span>
                            </button>
                          );
                        })()}
                        {['2026', '2027', '2028'].map(y => {
                          const active = fdcHighlight.includes(y);
                          const count = fdcCounts[y] || 0;
                          return (
                            <button key={y} type="button"
                              onClick={() => toggleFdc(y)}
                              className={`group flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-all ${active
                                ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/30 ring-2 ring-amber-300'
                                : 'border-slate-200 dark:border-slate-700 hover:border-amber-300 hover:bg-amber-50/50 dark:hover:bg-amber-900/10'}`}
                              data-testid={`ca-corner-fdc-${y}`}>
                              <span className={`flex items-center justify-center w-10 h-10 rounded-lg text-lg font-bold tabular-nums ${active ? 'bg-amber-400 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200'}`}>{count}</span>
                              <span>
                                <span className="block text-xs uppercase text-slate-400">Date FDC</span>
                                <span className="block text-sm font-semibold text-slate-700 dark:text-slate-200">{y}</span>
                              </span>
                            </button>
                          );
                        })}
                        {(() => {
                          const active = fdcHighlight.includes('2029plus');
                          const count = Object.entries(fdcCounts).reduce((s, [yy, n]) => (parseInt(yy, 10) >= 2029 ? s + n : s), 0);
                          return (
                            <button type="button"
                              onClick={() => toggleFdc('2029plus')}
                              className={`group flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-all ${active
                                ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/30 ring-2 ring-amber-300'
                                : 'border-slate-200 dark:border-slate-700 hover:border-amber-300 hover:bg-amber-50/50 dark:hover:bg-amber-900/10'}`}
                              data-testid="ca-corner-fdc-2029plus">
                              <span className={`flex items-center justify-center w-10 h-10 rounded-lg text-lg font-bold tabular-nums ${active ? 'bg-amber-400 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200'}`}>{count}</span>
                              <span>
                                <span className="block text-xs uppercase text-slate-400">Date FDC</span>
                                <span className="block text-sm font-semibold text-slate-700 dark:text-slate-200">2029+</span>
                              </span>
                            </button>
                          );
                        })()}
                        <div className="flex items-center gap-2 rounded-xl border border-slate-200 dark:border-slate-700 px-3 py-2">
                          <span className="text-xs uppercase text-slate-400">Année</span>
                          <input type="number" min="2020" max="2099" placeholder="ex. 2031"
                            value={fdcCustomYear}
                            onChange={(e) => setFdcCustomYear(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter' && /^20\d{2}$/.test(fdcCustomYear) && !fdcHighlight.includes(fdcCustomYear)) toggleFdc(fdcCustomYear); }}
                            className="w-24 bg-transparent text-sm outline-none border-b border-slate-200 dark:border-slate-700 focus:border-amber-400"
                            data-testid="ca-corner-fdc-custom-input" />
                          <button type="button"
                            disabled={!/^20\d{2}$/.test(fdcCustomYear)}
                            onClick={() => !fdcHighlight.includes(fdcCustomYear) && toggleFdc(fdcCustomYear)}
                            className="text-sm font-medium text-amber-700 hover:text-amber-900 disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid="ca-corner-fdc-custom-go">
                            {fdcCustomYear && (fdcCounts[fdcCustomYear] || 0) > 0 ? `Voir (${fdcCounts[fdcCustomYear]})` : 'Voir'}
                          </button>
                        </div>
                        {fdcHighlight.filter(k => !['avant2026', '2026', '2027', '2028', '2029plus'].includes(k)).map(k => (
                          <button key={k} type="button" onClick={() => toggleFdc(k)} data-testid={`ca-corner-fdc-chip-${k}`}
                            className="flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-800">
                            {k} ({fdcCounts[k] || 0}) <X className="w-3 h-3" />
                          </button>
                        ))}
                        {fdcHighlight.length > 0 && (
                          <span className="text-xs text-slate-500" data-testid="ca-corner-fdc-selected-count">
                            {filtered.filter(fdcMatch).length} corner(s) surligné(s)
                          </span>
                        )}
                        {fdcHighlight.length > 0 && (
                          <button type="button" onClick={() => { setFdcHighlight([]); setFdcCustomYear(''); }}
                            className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
                            data-testid="ca-corner-fdc-clear">
                            <X className="w-4 h-4" /> Retirer le surlignage
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </TooltipProvider>
          )}

          {/* Recherche + filtres + exports — juste au-dessus du tableau */}
          <div className="space-y-3" data-testid="ca-corner-filters">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input className="pl-9 pr-9" placeholder="Rechercher un corner…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="ca-corner-search" />
              {search && (
                <button onClick={() => setSearch('')} title="Effacer la recherche"
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                  data-testid="ca-corner-search-clear">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Select value={filterDr} onValueChange={setFilterDr}>
                <SelectTrigger className="w-[190px]" data-testid="ca-corner-filter-dr"><Users className="w-4 h-4 mr-1 text-slate-400" /><SelectValue placeholder="DR" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Tous les DR</SelectItem>
                  {drOptions.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterBm} onValueChange={setFilterBm}>
                <SelectTrigger className="w-[210px]" data-testid="ca-corner-filter-bm"><Users className="w-4 h-4 mr-1 text-slate-400" /><SelectValue placeholder="Business Manager" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Tous les Business Managers</SelectItem>
                  {bmOptions.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterTrend} onValueChange={setFilterTrend}>
                <SelectTrigger className="w-[160px]" data-testid="ca-corner-filter-trend"><Filter className="w-4 h-4 mr-1 text-slate-400" /><SelectValue placeholder="Tendance" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Toutes tendances</SelectItem>
                  <SelectItem value="up">En hausse</SelectItem>
                  <SelectItem value="down">En baisse</SelectItem>
                  <SelectItem value="nc">NC</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterQuality} onValueChange={setFilterQuality}>
                <SelectTrigger className="w-[175px]" data-testid="ca-corner-filter-quality"><Filter className="w-4 h-4 mr-1 text-slate-400" /><SelectValue placeholder="Qualité" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Toutes qualités</SelectItem>
                  <SelectItem value="green"><span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" /> Vert (≥ 95 %)</span></SelectItem>
                  <SelectItem value="orange"><span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-orange-500" /> Orange (90–94,99 %)</span></SelectItem>
                  <SelectItem value="red"><span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-red-500" /> Rouge (&lt; 90 %)</span></SelectItem>
                  <SelectItem value="none">Sans audit</SelectItem>
                </SelectContent>
              </Select>
              {filtersActive && (
                <Button variant="ghost" size="sm" onClick={resetFilters} className="gap-1 text-slate-500" data-testid="ca-corner-reset-filters"><RotateCcw className="w-4 h-4" /> Réinitialiser</Button>
              )}
              {hiddenNoCa > 0 && (
                <span className="text-xs text-slate-500 dark:text-slate-400 font-medium" data-testid="ca-corner-sanca-info">
                  {hiddenNoCa} corner(s) fermé(s) masqué(s)
                </span>
              )}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-1.5" data-testid="ca-corner-columns-btn">
                    <Columns3 className="w-4 h-4 text-slate-500" /> Colonnes
                    <span className="ml-0.5 text-xs text-slate-400">({activeCols.length}/{COLUMN_DEFS.length})</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-0" align="start" data-testid="ca-corner-columns-popover">
                  <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 dark:border-slate-800">
                    <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Colonnes affichées</span>
                    <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs text-slate-500"
                            onClick={resetCols} data-testid="ca-corner-columns-reset">
                      <RotateCcw className="w-3.5 h-3.5" /> Tout afficher
                    </Button>
                  </div>
                  <div className="max-h-72 overflow-y-auto py-1">
                    {COLUMN_DEFS.map(col => (
                      <label key={col.key}
                             className={`flex items-center gap-2.5 px-3 py-1.5 text-sm ${col.always ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800'}`}
                             data-testid={`ca-corner-col-opt-${col.key}`}>
                        <Checkbox checked={col.always || isColVisible(col.key)} disabled={col.always}
                                  onCheckedChange={() => !col.always && toggleCol(col.key)} />
                        <span className="text-slate-700 dark:text-slate-200">{col.label}</span>
                      </label>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
              <div className="flex gap-2 ml-auto">
                <Button variant="outline" onClick={() => handleExport('excel')} className="gap-2" data-testid="ca-corner-export-excel"><FileSpreadsheet className="w-4 h-4 text-emerald-600" /> Excel</Button>
                <Button variant="outline" onClick={() => handleExport('pdf')} className="gap-2" data-testid="ca-corner-export-pdf"><FileText className="w-4 h-4 text-red-600" /> PDF</Button>
              </div>
            </div>
          </div>

          <Card className="border-slate-200 dark:border-slate-800 overflow-hidden">
            <TooltipProvider delayDuration={100}>
            <div className="overflow-x-auto max-h-[65vh] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 z-10 bg-slate-100 dark:bg-slate-800 shadow-sm">
                  <tr>
                    {activeCols.map(col => (
                      <Th key={col.key} label={col.label} k={col.sortKey} tip={col.tip} align={col.align === 'right' ? 'right' : 'left'} sticky={col.sticky} />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={activeCols.length} className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></td></tr>
                  ) : filtered.length === 0 ? (
                    <tr><td colSpan={activeCols.length} className="text-center py-12 text-slate-500">Aucun corner ne correspond. Ajustez vos filtres ou importez un fichier CA.</td></tr>
                  ) : (qualityHighlight
                    ? [...filtered].sort((a, b) => (qualityZoneOf(b) === qualityHighlight) - (qualityZoneOf(a) === qualityHighlight))
                    : filtered).map((c, i) => {
                    const noCa = c.has_ca === false || c.ca_ht_monthly == null || c.ca_ht_monthly === 0;
                    const qHl = qualityHighlight && qualityZoneOf(c) === qualityHighlight ? qualityHighlight : null;
                    const rowBg = qHl ? QUALITY_ROW_HL[qHl] : fdcHighlight && fdcMatch(c)
                      ? 'bg-amber-100 dark:bg-amber-900/30'
                      : (selectedZone && c.zone === selectedZone ? ZONES[selectedZone].rowHl : (i % 2 ? 'bg-slate-50 dark:bg-slate-800/40' : 'bg-white dark:bg-slate-900'));
                    return (
                    <tr key={c.id}
                        onClick={() => handleRowClick(c.id)}
                        className={`cursor-pointer transition-colors hover:bg-indigo-50 dark:hover:bg-indigo-900/20 ${
                          qHl ? QUALITY_ROW_RING[qHl] : fdcHighlight && fdcMatch(c)
                            ? 'ring-1 ring-inset ring-amber-300'
                            : ''} ${rowBg} ${noCa ? 'opacity-60 hover:opacity-100' : ''}`}
                        data-testid={`ca-corner-row-${c.id}`}>
                      {activeCols.map(col => (
                        <td key={col.key} className={`${tdClass(col, false)} ${col.sticky ? `sticky left-0 z-[1] border-r border-slate-200 dark:border-slate-700 ${rowBg}` : ''}`}>{col.cell(c)}</td>
                      ))}
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </TooltipProvider>
            {!loading && filtered.length > 0 && (
              <div className="px-4 py-3 text-sm text-slate-500 border-t border-slate-200 dark:border-slate-800">
                {filtered.length} corner(s){lastImport ? ` · CA N cumulé à ${lastImport.month_label} ${lastImport.year}` : ''}{hiddenNoCa > 0 ? ` · ${hiddenNoCa} corner(s) fermé(s) masqué(s)` : ''} · <span className="text-slate-400">cliquez sur une ligne pour le détail</span>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="parc">
          <ParcAnalysisTab corners={corners} runRateAnnual={runRateAnnual} onRowClick={handleRowClick} token={token} canEdit={canEdit} updateCornerField={updateCornerField} />
        </TabsContent>

        <TabsContent value="perf">
          <PerformanceTab corners={corners} runRateAnnual={runRateAnnual} onRowClick={handleRowClick} canEdit={canEdit} updateCornerField={updateCornerField} />
        </TabsContent>

        <TabsContent value="tensions">
          <TensionTab tensions={tensions} cornersById={Object.fromEntries(corners.map(c => [c.id, c]))} canEdit={canEditTension} onEdit={setTensionCorner} />
        </TabsContent>

        {/* Vue Cockpit commercial */}
        <TabsContent value="cockpit" className="space-y-6" data-testid="ca-corner-cockpit">
          {cockpit.top3.length > 0 && (() => {
            const RANK = {
              1: { h: 168, grad: 'linear-gradient(180deg,#fbbf24,#f59e0b)', ring: 'ring-amber-300', text: 'text-amber-600', Icon: Crown, delay: '0.18s', delayMs: 180 },
              2: { h: 120, grad: 'linear-gradient(180deg,#cbd5e1,#94a3b8)', ring: 'ring-slate-300', text: 'text-slate-500', Icon: Medal, delay: '0s', delayMs: 0 },
              3: { h: 92, grad: 'linear-gradient(180deg,#fdba74,#b45309)', ring: 'ring-orange-300', text: 'text-orange-600', Icon: Medal, delay: '0.34s', delayMs: 340 },
            };
            const metricCfg = {
              ca: { label: 'CA cumulé', desc: 'CA cumulé année N', list: cockpit.top3, val: (c) => c.ca_n_current || 0, fmt: (v) => fmtEur(v) },
              trend: { label: 'Progression', desc: 'meilleure progression vs N-1', list: cockpit.topUp.slice(0, 3), val: (c) => c.trend || 0, fmt: (v) => `${v > 0 ? '+' : ''}${v.toFixed(1)} %` },
              m2: { label: 'CA au m²', desc: 'CA cumulé par m²', list: cockpit.perM2.slice(0, 3), val: (c) => c.caM2 || 0, fmt: (v) => `${fmtEur(v)}/m²` },
            };
            const mc = metricCfg[podiumMetric] || metricCfg.ca;
            const p = mc.list;
            const slots = [
              p[1] && { ...p[1], rank: 2 },
              p[0] && { ...p[0], rank: 1 },
              p[2] && { ...p[2], rank: 3 },
            ].filter(Boolean);
            return (
              <Card className="border-slate-200 dark:border-slate-800 overflow-hidden">
                <style>{`
                  @keyframes caPodiumRise { from { transform: scaleY(0); opacity:.2 } to { transform: scaleY(1); opacity:1 } }
                  @keyframes caPodiumPop { 0% { transform: translateY(14px) scale(.85); opacity:0 } 60% { transform: translateY(-3px) scale(1.05); opacity:1 } 100% { transform: translateY(0) scale(1); opacity:1 } }
                  @keyframes caCrownFloat { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-4px) } }
                  .ca-podium-bar { transform-origin: bottom; animation: caPodiumRise .75s cubic-bezier(.22,1,.36,1) both; }
                  .ca-podium-info { animation: caPodiumPop .6s ease both; }
                  .ca-podium-crown { animation: caCrownFloat 2.4s ease-in-out infinite; }
                `}</style>
                <CardHeader className="pb-0">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <CardTitle className="text-base flex items-center gap-2"><Trophy className="w-4 h-4 text-amber-500" /> Podium — Top 3 · {mc.label} <InfoTip text="Top 3 des corners selon le critère sélectionné (CA cumulé N, progression vs N-1, ou CA au m²). France métropole uniquement (Réunion exclue). Source : dernier import Excel mensuel." /></CardTitle>
                      <CardDescription>Meilleures performances commerciales ({mc.desc}{cockpit.top3[0]?.ca_n_month_label ? ` — à ${cockpit.top3[0].ca_n_month_label}` : ''})</CardDescription>
                    </div>
                    <div className="inline-flex rounded-lg bg-slate-100 dark:bg-slate-800 p-1" data-testid="ca-corner-podium-metric">
                      {Object.entries(metricCfg).map(([k, m]) => (
                        <button key={k} onClick={() => setPodiumMetric(k)}
                                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${podiumMetric === k ? 'bg-white dark:bg-slate-900 shadow text-indigo-600' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}`}
                                data-testid={`ca-corner-podium-metric-${k}`}>{m.label}</button>
                      ))}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-8 pb-6">
                  {slots.length === 0 ? (
                    <p className="text-sm text-slate-400 py-8 text-center">Données insuffisantes pour ce classement.</p>
                  ) : (
                  <div className="flex items-end justify-center gap-4 sm:gap-8" data-testid="ca-corner-podium">
                    {slots.map((s) => {
                      const cfg = RANK[s.rank];
                      const Icon = cfg.Icon;
                      const value = mc.val(s);
                      return (
                        <button key={`${podiumMetric}-${s.id}`} onClick={() => handleRowClick(s.id)}
                                className="flex flex-col items-center w-28 sm:w-44 group focus:outline-none"
                                data-testid={`ca-corner-podium-${s.rank}`}>
                          <div className="ca-podium-info flex flex-col items-center mb-2" style={{ animationDelay: cfg.delay }}>
                            <div className={`relative mb-2 ${s.rank === 1 ? 'ca-podium-crown' : ''}`}>
                              <Icon className={`w-8 h-8 sm:w-10 sm:h-10 ${cfg.text}`} strokeWidth={2.2} />
                            </div>
                            <p className="text-sm sm:text-[15px] font-semibold text-center leading-tight text-slate-800 dark:text-slate-100 line-clamp-2 group-hover:text-indigo-600 transition-colors">
                              {s.name_powerbi || s.name_sf}
                            </p>
                            <p className="text-base sm:text-lg font-bold font-mono tabular-nums text-slate-900 dark:text-white mt-0.5">
                              <CountUp key={`${podiumMetric}-${s.id}`} value={value} format={mc.fmt} delay={cfg.delayMs} />
                            </p>
                            {s.nom_dr && <p className="text-[11px] text-slate-400 truncate max-w-full">{s.nom_dr}</p>}
                          </div>
                          <div className={`ca-podium-bar w-full rounded-t-xl flex items-start justify-center pt-2 shadow-sm ring-1 ${cfg.ring}`}
                               style={{ height: cfg.h, background: cfg.grad, animationDelay: cfg.delay }}>
                            <span className="text-3xl sm:text-4xl font-black text-white/90 drop-shadow">{s.rank}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  )}
                </CardContent>
              </Card>
            );
          })()}

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat icon={Store} label="Corners" value={cockpit.count} color="indigo" info="Nombre de corners retenus dans le Cockpit (France métropole uniquement — les magasins de l'Île de la Réunion, enseigne « Carrefour R », sont exclus). Source : dernier import Excel mensuel du CA." />
            <Stat icon={Euro} label="CA N cumulé total" value={fmtEur(cockpit.totalCA)} color="emerald" info="Somme du CA HT cumulé année N (colonne « CA HT CUMUL N ») de tous les corners métropole. Source : import Excel mensuel." />
            <Stat icon={TrendingUp} label="En hausse" value={cockpit.up} color="emerald" info="Corners dont la tendance vs N-1 est positive. Tendance = CA cumul TTC (CA HT cumulé × 1,10) comparé au prorata du CA N-1 (déjà TTC) sur les mois écoulés." />
            <Stat icon={TrendingDown} label="En baisse" value={cockpit.down} color="red" info="Corners dont la tendance vs N-1 est négative (même calcul que « En hausse »)." />
          </div>

          <Card className="border-slate-200 dark:border-slate-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2"><Users className="w-4 h-4 text-indigo-600" /> CA N cumulé par Directeur Régional <InfoTip text="Somme du CA HT cumulé année N par DR (colonne « Nom du DR »). Respecte les filtres actifs. France métropole uniquement." /></CardTitle>
              <CardDescription>Somme du CA en cours par DR (parc global — hors filtres du tableau)</CardDescription>
            </CardHeader>
            <CardContent>
              {cockpit.byDr.length === 0 ? (
                <p className="text-sm text-slate-400 py-8 text-center">Aucune donnée.</p>
              ) : (
                <div style={{ width: '100%', height: 320 }} data-testid="ca-corner-dr-chart">
                  <ResponsiveContainer>
                    <BarChart data={cockpit.byDr.slice(0, 12)} margin={{ top: 8, right: 12, left: 0, bottom: 70 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                      <XAxis dataKey="dr" angle={-30} textAnchor="end" interval={0} height={80} tick={{ fontSize: 11, fill: '#64748b' }} />
                      <YAxis tickFormatter={(v) => `${Math.round(v / 1000)}k`} tick={{ fontSize: 11, fill: '#64748b' }} />
                      <Tooltip formatter={(v) => fmtEur(v)} labelStyle={{ fontWeight: 600 }} />
                      <Bar dataKey="ca" radius={[4, 4, 0, 0]} fill="#4F46E5" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <RankList
              title="🥇 Top 10 — Meilleur CA TTC N" testid="ca-corner-top-ttc-n"
              info="Classement par CA TTC cumulé année N (= CA HT CUMUL N × 1,10, TVA 10 %). Décroissant. Source : dernier import Excel mensuel. France métropole uniquement."
              items={cockpit.topTtcN} empty="Aucun CA disponible."
              render={(c) => (
                <>
                  <span className="flex-1 min-w-0 truncate font-medium text-slate-800 dark:text-slate-100">{c.name_powerbi || c.name_sf}</span>
                  <span className="font-mono font-semibold text-indigo-600 dark:text-indigo-400 whitespace-nowrap">{fmtEur(ttc(c.ca_n_current))}</span>
                </>
              )}
            />
            <RankList
              title="🏅 Top 10 — Meilleur CA TTC N-1" testid="ca-corner-top-ttc-n1"
              info="Classement par CA TTC de l'année précédente (colonne « CA N-1 TTC », déjà en TTC dans le fichier). Décroissant. Renseigné lors de l'import de référence."
              items={cockpit.topTtcN1} empty="CA N-1 non renseigné (import de référence)."
              render={(c) => (
                <>
                  <span className="flex-1 min-w-0 truncate font-medium text-slate-800 dark:text-slate-100">{c.name_powerbi || c.name_sf}</span>
                  <span className="font-mono font-semibold text-slate-700 dark:text-slate-200 whitespace-nowrap">{fmtEur(c.ca_n1)}</span>
                </>
              )}
            />
            <RankList
              title="📈 Top 10 — Meilleure progression d'un mois à l'autre" testid="ca-corner-top-mom"
              info="Progression du CA mensuel entre les 2 derniers mois importés : (CA du mois − CA du mois précédent) ÷ CA du mois précédent. Nécessite au moins 2 imports mensuels."
              items={cockpit.topMom} empty="Nécessite au moins 2 imports mensuels."
              render={(c) => (
                <>
                  <span className="flex-1 min-w-0 truncate font-medium text-slate-800 dark:text-slate-100">{c.name_powerbi || c.name_sf}</span>
                  <span className={`font-semibold whitespace-nowrap ${c.mom >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{c.mom > 0 ? '+' : ''}{c.mom.toFixed(1)}%</span>
                </>
              )}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <RankList
              title="🚀 Top progressions (vs N-1)" testid="ca-corner-top-up"
              info="Meilleures tendances vs N-1. Tendance = CA cumul TTC (CA HT cumulé × 1,10) vs prorata du CA N-1 (TTC) sur les mois écoulés. NC si N-1/N-2 absents."
              items={cockpit.topUp} empty="Aucune tendance disponible."
              render={(c) => (
                <>
                  <span className="flex-1 min-w-0 truncate font-medium text-slate-800 dark:text-slate-100">{c.name_powerbi || c.name_sf}</span>
                  <span className={`font-semibold whitespace-nowrap ${c.trend >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{c.trend > 0 ? '+' : ''}{c.trend.toFixed(1)}%</span>
                </>
              )}
            />
            <RankList
              title="📉 Plus fortes baisses (vs N-1)" testid="ca-corner-top-down"
              info="Plus fortes baisses de tendance vs N-1 (même calcul que « Top progressions »)."
              items={cockpit.topDown} empty="Aucune tendance disponible."
              render={(c) => (
                <>
                  <span className="flex-1 min-w-0 truncate font-medium text-slate-800 dark:text-slate-100">{c.name_powerbi || c.name_sf}</span>
                  <span className={`font-semibold whitespace-nowrap ${c.trend >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{c.trend > 0 ? '+' : ''}{c.trend.toFixed(1)}%</span>
                </>
              )}
            />
            <RankList
              title="📐 Meilleur CA au m²" testid="ca-corner-per-m2"
              info="CA cumulé année N (HT) ÷ surface du corner (m²). Décroissant. La surface provient de la colonne « Surface en m2 » du fichier."
              items={cockpit.perM2} empty="Renseignez la surface des corners (import)."
              render={(c) => (
                <>
                  <span className="flex-1 min-w-0 truncate font-medium text-slate-800 dark:text-slate-100">{c.name_powerbi || c.name_sf}</span>
                  <span className="text-slate-400 text-xs whitespace-nowrap mr-2">{fmtSurface(c.surface_m2)}</span>
                  <span className="font-mono font-semibold text-slate-700 dark:text-slate-200 whitespace-nowrap">{fmtEur(c.caM2)}/m²</span>
                </>
              )}
            />
            <Card className="border-slate-200 dark:border-slate-800">
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Trophy className="w-4 h-4 text-amber-500" /> Classement des DR</CardTitle></CardHeader>
              <CardContent>
                {cockpit.byDr.length === 0 ? (
                  <p className="text-sm text-slate-400 py-6 text-center">Aucune donnée.</p>
                ) : (
                  <div className="overflow-x-auto" data-testid="ca-corner-dr-ranking">
                    <table className="w-full text-sm">
                      <thead><tr className="text-left text-xs uppercase text-slate-400">
                        <th className="py-2 pr-2">DR</th><th className="py-2 px-2 text-right">Corners</th>
                        <th className="py-2 px-2 text-right">CA N</th><th className="py-2 pl-2 text-right">Tend. moy.</th>
                      </tr></thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                        {cockpit.byDr.map((d) => (
                          <tr key={d.dr}>
                            <td className="py-2 pr-2 font-medium truncate max-w-[160px]">{d.dr}</td>
                            <td className="py-2 px-2 text-right tabular-nums">{d.count}</td>
                            <td className="py-2 px-2 text-right font-mono tabular-nums">{fmtEur(d.ca)}</td>
                            <td className="py-2 pl-2 text-right"><TrendCell v={d.trendAvg} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Onglet Suivi Facturation NEW */}
        <TabsContent value="facturation" className="space-y-4">
          <Card className="border-yellow-300 dark:border-yellow-800 bg-yellow-50/40 dark:bg-yellow-900/10">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <CardTitle className="text-base flex items-center gap-2 text-yellow-800 dark:text-yellow-300">
                    <FileText className="w-4 h-4" /> Suivi Facturation — Nouveaux clients
                    <InfoTip text="Corners installés récemment : uniquement ceux dont la « Date de 1er contrat » tombe dans les 6 derniers mois entiers ou le mois en cours (à partir du 1er jour du mois M-6). Passé ce délai, la ligne disparaît. Objectif : suivre la montée en facturation sur les premiers mois. Une alerte email est envoyée au bout de 3 mois pleins si l'atterrissage est inférieur de plus de 15 % à la prévision. Prévi CA annuel HT = champ libre éditable ; les écarts et le run-rate sont calculés automatiquement." />
                  </CardTitle>
                  <CardDescription>{newClients.length} nouveau(x) client(s) suivi(s) · fenêtre ouverture + 6 mois</CardDescription>
                </div>
                {isAdmin && newClients.length > 0 && (
                  <Button variant="outline" size="sm" onClick={handleExportFacturation}
                    className="gap-2 border-yellow-400 text-yellow-800 hover:bg-yellow-100 dark:border-yellow-700 dark:text-yellow-300"
                    data-testid="ca-corner-fact-export-excel">
                    <FileSpreadsheet className="w-4 h-4 text-emerald-600" /> Excel
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {lateNewClients.length > 0 && (
                <div className="mx-4 mb-3 flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-900/20 px-4 py-3" data-testid="ca-corner-late-banner">
                  <div className="flex items-start gap-2 flex-1">
                    <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                    <p className="text-sm text-red-800 dark:text-red-300">
                      <span className="font-semibold">{lateNewClients.length} nouveau(x) client(s) en retard</span> — atterrissage (run-rate) inférieur de plus de 15 % à la prévision après au moins 3 mois : {lateNewClients.map(c => c.name_powerbi || c.name_sf).join(', ')}.
                    </p>
                  </div>
                  {isAdmin && (
                    <Button size="sm" variant="outline" onClick={sendLateAlert} disabled={sendingAlert}
                      className="shrink-0 border-red-300 text-red-700 hover:bg-red-100 dark:border-red-800 dark:text-red-300"
                      data-testid="ca-corner-send-late-alert-btn">
                      {sendingAlert ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      <span className="ml-1.5">Alerter les Super Admins</span>
                    </Button>
                  )}
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 z-10 bg-yellow-100 dark:bg-yellow-900/40">
                    <tr>
                      {['Corner', 'Compte SF', 'Date 1er contrat', 'Mois', 'CA HT', 'CA TTC', 'CA HT CUMUL N',
                        'Prévi CA annuel HT', 'Prévi CA annuel TTC', 'Écart mois €', 'Écart mois %',
                        'Écart cumulé €', 'Écart cumulé %', 'Atterrissage (run-rate)', 'Moyenne qualité',
                        'Achat', 'Ratio Saumon', 'Taux de casse', 'Rendement', 'Productivité', 'Responsable dossier'].map((h, i) => (
                        <th key={i} className={`px-3 py-2.5 font-semibold text-slate-600 dark:text-slate-300 whitespace-nowrap ${['CA HT','CA TTC','CA HT CUMUL N','Prévi CA annuel HT','Prévi CA annuel TTC','Écart mois €','Écart mois %','Écart cumulé €','Écart cumulé %'].includes(h) ? 'text-right' : (['Moyenne qualité','Achat','Ratio Saumon','Taux de casse','Rendement','Productivité'].includes(h) ? 'text-center' : 'text-left')}`}>
                          {h === 'Atterrissage (run-rate)' ? (
                            <span className="inline-flex items-center gap-1 justify-end">
                              {h}
                              <InfoTip text="Projection du CA annuel HT (atterrissage) calculée au jour près : Run-rate = CA HT cumulé N ÷ nombre de jours écoulés depuis le départ × nombre de jours de l'année. Départ = date d'ouverture (Date 1er contrat) si le corner a ouvert cette année ; sinon 1er janvier (notamment si un CA N-1 existe, le CA est réputé généré depuis le 1er janvier). Badge : En avance (≥ +15 % vs Prévi annuel HT), Conforme (entre -15 % et +15 %), En retard (≤ -15 %). Fiable à partir de 3 mois pleins." />
                            </span>
                          ) : h === 'Moyenne qualité' ? (
                            <span className="inline-flex items-center gap-1 justify-center">
                              {h}
                              <InfoTip text="Moyenne des audits qualité du corner. Code couleur : vert ≥ 95 %, orange 90–94,99 %, rouge < 90 %. Survolez le badge pour voir le détail des audits (mois et scores)." />
                            </span>
                          ) : h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {newClients.length === 0 ? (
                      <tr><td colSpan={21} className="text-center py-12 text-slate-500">Aucun nouveau client sur les 6 derniers mois. Renseignez la « Date de 1er contrat » d'un corner pour le voir apparaître ici.</td></tr>
                    ) : newClients.map((c, idx) => {
                      const previHT = c.previ_ca_annuel_ht;
                      const previMens = previHT ? previHT / 12 : null;
                      const me = monthsElapsed(c);
                      const previProrata = (previHT && me) ? previHT * me / 12 : null;
                      const ecartMoisE = (previMens && c.ca_ht_monthly != null) ? c.ca_ht_monthly - previMens : null;
                      const ecartMoisP = (ecartMoisE != null && previMens) ? ecartMoisE / previMens * 100 : null;
                      const ecartCumE = (previProrata && c.ca_n_current != null) ? c.ca_n_current - previProrata : null;
                      const ecartCumP = (ecartCumE != null && previProrata) ? ecartCumE / previProrata * 100 : null;
                      const runRate = runRateAnnual(c);
                      const rrDelta = (runRate != null && previHT) ? (runRate - previHT) / previHT * 100 : null;
                      const rrStatus = rrDelta == null ? null : (rrDelta >= 15 ? 'up' : (rrDelta <= -15 ? 'down' : 'ok'));
                      const eColor = (v) => v == null ? 'text-slate-400' : (v >= 0 ? 'text-emerald-600' : 'text-red-600');
                      return (
                        <tr key={c.id} className={idx % 2 ? 'bg-slate-50/60 dark:bg-slate-800/30' : 'bg-white dark:bg-slate-900'} data-testid={`ca-corner-fact-row-${c.id}`}>
                          <td className="px-3 py-2 font-semibold text-slate-800 dark:text-slate-100 whitespace-nowrap cursor-pointer hover:text-indigo-600" onClick={() => handleRowClick(c.id)}>{c.name_powerbi || '—'}</td>
                          <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{c.name_sf || '—'}</td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <input type="date" defaultValue={toISODate(c.date_contrat)} disabled={!canEdit}
                              key={`d-${c.id}-${c.date_contrat || ''}`}
                              onChange={(e) => updateCornerField(c.id, { date_contrat: e.target.value })}
                              className="px-2 py-1 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 disabled:opacity-60"
                              data-testid={`ca-corner-fact-date-${c.id}`} />
                            {contractAfterPeriod(c) && (
                              <span className="mt-1 flex items-center gap-1 text-[11px] font-semibold text-amber-700 dark:text-amber-400"
                                title="La Date de 1er contrat est postérieure au mois de CA importé. Le CA cumulé ne peut pas précéder l'ouverture : vérifiez cette date. Le run-rate est calculé depuis le 1er janvier en attendant."
                                data-testid={`ca-corner-date-warn-${c.id}`}>
                                <AlertTriangle className="w-3 h-3" /> date à vérifier
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-slate-600 dark:text-slate-300 whitespace-nowrap">{fmtText(c.ca_n_month_label)}</td>
                          <td className="px-3 py-2 text-right font-mono tabular-nums whitespace-nowrap">{fmtEur(c.ca_ht_monthly)}</td>
                          <td className="px-3 py-2 text-right font-mono tabular-nums whitespace-nowrap text-indigo-600 dark:text-indigo-400">{fmtEur(ttc(c.ca_ht_monthly))}</td>
                          <td className="px-3 py-2 text-right font-mono tabular-nums whitespace-nowrap">{fmtEur(c.ca_n_current)}</td>
                          <td className="px-3 py-2 text-right whitespace-nowrap">
                            <input type="number" step="1" min="0" defaultValue={previHT ?? ''} disabled={!canEdit}
                              key={`p-${c.id}-${previHT ?? ''}`}
                              onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                              onBlur={(e) => { const v = e.target.value; if (String(previHT ?? '') !== v) updateCornerField(c.id, { previ_ca_annuel_ht: v === '' ? '' : Number(v) }); }}
                              placeholder="—"
                              className="w-32 px-2 py-1 rounded border border-yellow-300 dark:border-yellow-700 bg-white dark:bg-slate-800 text-right font-mono text-slate-800 dark:text-slate-100 disabled:opacity-60"
                              data-testid={`ca-corner-fact-previ-${c.id}`} />
                          </td>
                          <td className="px-3 py-2 text-right font-mono tabular-nums whitespace-nowrap text-slate-500">{previHT ? fmtEur(ttc(previHT)) : '—'}</td>
                          <td className={`px-3 py-2 text-right font-mono tabular-nums whitespace-nowrap font-medium ${eColor(ecartMoisE)}`}>{ecartMoisE == null ? '—' : fmtEur(ecartMoisE)}</td>
                          <td className={`px-3 py-2 text-right font-mono tabular-nums whitespace-nowrap ${eColor(ecartMoisP)}`}>{fmtPct1(ecartMoisP)}</td>
                          <td className={`px-3 py-2 text-right font-mono tabular-nums whitespace-nowrap font-medium ${eColor(ecartCumE)}`}>{ecartCumE == null ? '—' : fmtEur(ecartCumE)}</td>
                          <td className={`px-3 py-2 text-right font-mono tabular-nums whitespace-nowrap ${eColor(ecartCumP)}`}>{fmtPct1(ecartCumP)}</td>
                          <td className="px-3 py-2 text-right whitespace-nowrap">
                            {runRate == null ? <span className="text-slate-400">—</span> : (
                              <span className="inline-flex items-center gap-1.5">
                                <span className="font-mono tabular-nums text-slate-700 dark:text-slate-200">{fmtEur(runRate)}</span>
                                {rrStatus && <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${rrStatus === 'up' ? 'bg-emerald-100 text-emerald-700' : rrStatus === 'down' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-800'}`}>{rrStatus === 'up' ? 'En avance' : rrStatus === 'down' ? 'En retard' : 'Conforme'}</span>}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-center whitespace-nowrap" data-testid={`ca-corner-fact-quality-${c.id}`}>
                            <QualityBadge value={c.quality_avg} detail={c.quality_detail} />
                          </td>
                          <td className="px-3 py-2 text-center whitespace-nowrap"><RatioBadge value={c.foodcost} objective={RATIO_OBJECTIVES.foodcost} /></td>
                          <td className="px-3 py-2 text-center whitespace-nowrap"><RatioBadge value={c.ratio_saumon} objective={RATIO_OBJECTIVES.ratio_saumon} digits={2} /></td>
                          <td className="px-3 py-2 text-center whitespace-nowrap"><RatioBadge value={c.taux_casse} objective={RATIO_OBJECTIVES.taux_casse} /></td>
                          <td className="px-3 py-2 text-center whitespace-nowrap"><RatioBadge value={c.rendement_saumon} objective={230} higherIsBetter suffix=" €/kg" digits={0} /></td>
                          <td className="px-3 py-2 text-center whitespace-nowrap"><RatioBadge value={c.productivite} objective={60} higherIsBetter suffix=" €/h" digits={1} /></td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <select
                              value={c.responsable_user_id || ''}
                              disabled={!canEdit}
                              onChange={(e) => updateCornerField(c.id, { responsable_user_id: e.target.value || null })}
                              className="px-2 py-1 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-sm max-w-[180px] disabled:opacity-60"
                              data-testid={`ca-corner-fact-responsable-${c.id}`}>
                              <option value="">— Non affecté —</option>
                              {responsables.map(u => (
                                <option key={u.id} value={u.id}>{u.name}</option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Onglet Historique alertes */}
        <TabsContent value="alert-history" className="space-y-4">
          {/* Courbe run-rate par corner suivi */}
          <Card className="border-slate-200 dark:border-slate-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-indigo-600" /> Courbe d'atterrissage (run-rate)
                <InfoTip text="Évolution mois par mois de l'atterrissage projeté (run-rate) d'un corner suivi, comparée à sa prévision annuelle HT (ligne pointillée). Permet de voir si le corner se redresse d'un mois sur l'autre." />
              </CardTitle>
              <CardDescription>Sélectionnez un corner suivi pour visualiser l'évolution de son atterrissage vs sa prévision.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <select
                value={rrCornerId}
                onChange={(e) => loadRunrate(e.target.value)}
                className="px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-sm max-w-md"
                data-testid="ca-corner-rr-select">
                <option value="">— Choisir un corner suivi —</option>
                {newClients.filter(c => c.previ_ca_annuel_ht).map(c => (
                  <option key={c.id} value={c.id}>{c.name_powerbi || c.name_sf}</option>
                ))}
              </select>
              {rrLoading ? (
                <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
              ) : rrData && rrData.series && rrData.series.length > 0 ? (
                <div data-testid="ca-corner-rr-chart">
                  <ResponsiveContainer width="100%" height={320}>
                    <LineChart data={rrData.series} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="period" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                      <Tooltip formatter={(v) => fmtEur(v)} labelStyle={{ fontWeight: 600 }} />
                      <Legend />
                      {rrData.previ_ca_annuel_ht && (
                        <ReferenceLine y={rrData.previ_ca_annuel_ht} stroke="#f59e0b" strokeDasharray="6 4"
                          label={{ value: 'Prévi annuel HT', position: 'insideTopRight', fill: '#b45309', fontSize: 11 }} />
                      )}
                      <Line type="monotone" dataKey="run_rate" name="Run-rate projeté" stroke="#4f46e5" strokeWidth={2.5} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                    </LineChart>
                  </ResponsiveContainer>
                  <p className="text-xs text-slate-400 mt-2">
                    Prévi annuel HT : {fmtEur(rrData.previ_ca_annuel_ht)} · Date 1er contrat : {fmtDateFR(rrData.date_contrat)}
                  </p>
                </div>
              ) : rrCornerId ? (
                <p className="py-10 text-center text-sm text-slate-400">Aucun historique disponible pour ce corner.</p>
              ) : (
                <p className="py-10 text-center text-sm text-slate-400">Choisissez un corner pour afficher sa courbe.</p>
              )}
            </CardContent>
          </Card>

          {/* Journal des alertes envoyées */}
          <Card className="border-slate-200 dark:border-slate-800">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <History className="w-4 h-4 text-red-600" /> Journal des alertes envoyées
                </CardTitle>
                <CardDescription>{alertLog.length} alerte(s) tracée(s) · déclenchement automatique (lundi) ou manuel.</CardDescription>
              </div>
              <Button size="sm" variant="outline" onClick={loadAlertLog} disabled={alertLogLoading} data-testid="ca-corner-alert-log-refresh">
                {alertLogLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                <span className="ml-1.5">Actualiser</span>
              </Button>
            </CardHeader>
            <CardContent className="space-y-3">
              {alertLogLoading ? (
                <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
              ) : alertLog.length === 0 ? (
                <p className="py-10 text-center text-sm text-slate-400">Aucune alerte envoyée pour le moment.</p>
              ) : alertLog.map((log) => (
                <div key={log.id} className="rounded-lg border border-slate-200 dark:border-slate-700 p-4" data-testid={`ca-corner-alert-log-${log.id}`}>
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${log.trigger === 'manual' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`}>
                      {log.trigger === 'manual' ? 'Manuel' : 'Automatique'}
                    </span>
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-200">{new Date(log.run_at).toLocaleString('fr-FR')}</span>
                    <span className="text-xs text-slate-400">par {log.triggered_by}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-semibold">{log.late_count} en retard</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${log.emails_sent ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{log.emails_sent} email(s) envoyé(s)</span>
                    <span className="text-xs text-slate-400">seuil {log.threshold_pct}% · ≥ {log.min_months} mois</span>
                  </div>
                  {log.recipients && log.recipients.length > 0 && (
                    <p className="text-xs text-slate-500 mb-2">
                      Destinataires : {log.recipients.map(r => `${r.email}${r.kind === 'responsable' ? ' (responsable)' : ''}`).join(', ')}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-1.5">
                    {(log.corners || []).map((c, i) => (
                      <span key={i} className="text-xs px-2 py-1 rounded bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300">
                        <span className="font-medium">{c.name}</span> · {c.delta_pct != null ? `${c.delta_pct > 0 ? '+' : ''}${c.delta_pct.toFixed(1)}%` : '—'}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>


      {/* Dialog liste des corners d'une zone */}
      <Dialog open={zoneDialog} onOpenChange={setZoneDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="ca-corner-zone-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedZone && <span className="w-3 h-3 rounded-full" style={{ background: ZONES[selectedZone].dot }} />}
              Corners — {selectedZone ? ZONES[selectedZone].label : ''}
            </DialogTitle>
            <DialogDescription>
              {selectedZone ? `${zoneData.groups[selectedZone].length} corner(s) · les lignes correspondantes sont surlignées dans le tableau.` : ''}
            </DialogDescription>
          </DialogHeader>
          {selectedZone && (
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              {zoneData.groups[selectedZone].map((c, i) => (
                <button key={c.id} onClick={() => { setZoneDialog(false); handleRowClick(c.id); }}
                        className="w-full flex items-center gap-3 px-1 py-2.5 text-left hover:bg-indigo-50/60 dark:hover:bg-indigo-900/20 rounded transition-colors"
                        data-testid={`ca-corner-zone-item-${c.id}`}>
                  <span className="w-6 h-6 shrink-0 rounded-full text-xs font-bold flex items-center justify-center bg-slate-100 dark:bg-slate-800 text-slate-500">{i + 1}</span>
                  <span className="flex-1 min-w-0">
                    <span className="block truncate font-medium text-slate-800 dark:text-slate-100">{c.name_powerbi || c.name_sf}</span>
                    <span className="block text-xs text-slate-400 truncate">{c.city || '—'}</span>
                  </span>
                  <span className="font-mono font-semibold text-indigo-600 dark:text-indigo-400 whitespace-nowrap">{fmtEur(ttc(c.ca_n_current))}</span>
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Détail corner */}
      {/* Dialog Historique des imports */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="ca-corner-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><History className="w-5 h-5 text-indigo-600" /> Historique des imports</DialogTitle>
            <DialogDescription>Tous les imports validés — qui, quand, et le bilan des lignes importées.</DialogDescription>
          </DialogHeader>
          {historyLoading ? (
            <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
          ) : history.length === 0 ? (
            <p className="py-12 text-center text-slate-500">Aucun import validé pour le moment.</p>
          ) : (
            <div className="space-y-3" data-testid="ca-corner-history-list">
              {history.map((imp, i) => {
                const rep = imp.import_report || {};
                const ni = rep.not_imported_count || 0;
                return (
                  <div key={imp.id || i} className="rounded-lg border border-slate-200 dark:border-slate-700 p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-800 dark:text-slate-100">{imp.month_label} {imp.year}</p>
                        <p className="text-xs text-slate-400 truncate">
                          {(imp.validated_at || '').slice(0, 10)} · {imp.validated_by || imp.uploaded_by} · {imp.filename || '—'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">{imp.corners_updated || 0} MAJ</span>
                        <span className="text-xs px-2 py-1 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400">{imp.corners_created || 0} créé(s)</span>
                        <span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">{imp.corners_skipped || 0} ignoré(s)</span>
                        <span className={`text-xs px-2 py-1 rounded-full ${ni > 0 ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' : 'bg-slate-100 text-slate-500 dark:bg-slate-800'}`}>{ni} non importée(s)</span>
                        {ni > 0 && (
                          <Button variant="outline" size="sm" className="gap-1 h-7"
                                  onClick={() => buildReportCsv(rep.not_imported, `${imp.month_label}_${imp.year}`)}
                                  data-testid={`ca-corner-history-csv-${i}`}>
                            <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600" /> Rapport CSV
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>


      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="ca-corner-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Store className="w-5 h-5 text-indigo-600" />
              {detailCorner ? (detailCorner.name_powerbi || detailCorner.name_sf) : 'Chargement…'}
            </DialogTitle>
            <DialogDescription>Détail du chiffre d'affaires et historique mensuel du corner.</DialogDescription>
          </DialogHeader>
          {!detailCorner ? (
            <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
          ) : (
            <div className="space-y-4">
              {detailCorner.name_sf && <p className="text-sm text-slate-500">Compte SF : <span className="font-medium text-slate-700 dark:text-slate-200">{detailCorner.name_sf}</span></p>}
              <TooltipProvider delayDuration={100}>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <StatBox label={`CA HT ${detailCorner.ca_n_month_label ? `(${detailCorner.ca_n_month_label})` : '(mois)'}`} value={fmtEur(detailCorner.ca_ht_monthly)} tip="CA hors taxes du seul mois affiché." />
                <StatBox label="CA Mois TTC" valueClass="text-indigo-600 dark:text-indigo-400" value={fmtEur(ttc(detailCorner.ca_ht_monthly))} tip="CA du mois affiché, TTC = CA HT + TVA 10 %." />
                <StatBox label="Tendance vs N-1" valueClass="" value={<TrendCell v={detailCorner.trend} />} tip="Compare le CA cumulé N (en TTC) au prorata de N-1 sur la même période (N-1 × mois écoulés / 12). « NC » si N-2 absent." />
                <StatBox label={`CA HT CUMUL N${detailCorner.ca_n_month_label ? ` (à ${detailCorner.ca_n_month_label})` : ''}`} value={fmtEur(detailCorner.ca_n_current)} tip="CA hors taxes cumulé de l'année en cours (N), de janvier jusqu'au mois affiché." />
                <StatBox label="CA N. CUMUL TTC" valueClass="text-indigo-600 dark:text-indigo-400" value={fmtEur(ttc(detailCorner.ca_n_current))} tip="CA cumulé de l'année en cours (N) en TTC (HT + TVA 10 %), de janvier jusqu'au mois affiché." />
                <StatBox label="CA € au m²" valueClass="text-indigo-600 dark:text-indigo-400" value={fmtEurM2(caTtcM2(detailCorner))} tip="Performance au m² : CA TTC du mois affiché ÷ surface du corner (€/m²)." />
                <StatBox label="CA N-1 TTC" value={fmtEur(detailCorner.ca_n1)} tip="CA TTC de l'année complète précédente (N-1, 12 mois). Référence figée lors du 1er import." />
                <StatBox label="CA N-2 TTC" value={fmtEur(detailCorner.ca_n2)} tip="CA TTC de l'année complète il y a deux ans (N-2, 12 mois). Référence figée lors du 1er import." />
              </div>
              </TooltipProvider>
              {/* Graphique comparatif de tendance */}
              <div>
                <p className="text-sm font-semibold mb-2">Comparaison du CA (période vs références)</p>
                <div style={{ width: '100%', height: 210 }} data-testid="ca-corner-detail-chart">
                  <ResponsiveContainer>
                    <BarChart
                      data={[
                        { name: 'CA N', value: detailCorner.ca_n_current || 0 },
                        { name: 'N-1 prorata', value: (detailCorner.ca_n1 && detailCorner.ca_n_month) ? Math.round(detailCorner.ca_n1 * detailCorner.ca_n_month / 12) : 0 },
                        { name: 'N-1 année', value: detailCorner.ca_n1 || 0 },
                        { name: 'N-2 année', value: detailCorner.ca_n2 || 0 },
                      ]}
                      margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#64748b' }} />
                      <YAxis tickFormatter={(v) => `${Math.round(v / 1000)}k`} tick={{ fontSize: 10, fill: '#64748b' }} />
                      <Tooltip formatter={(v) => fmtEur(v)} />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                        {['#4F46E5', '#f59e0b', '#94a3b8', '#cbd5e1'].map((col, i) => <Cell key={i} fill={col} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <p className="text-xs text-slate-400 mt-1">La tendance compare le CA N cumulé au prorata de N-1 sur la même période{detailCorner.ca_n_month ? ` (${detailCorner.ca_n_month} mois)` : ''}.</p>
              </div>
              <div>
                <p className="text-sm font-semibold mb-2">Informations corner</p>
                <TooltipProvider delayDuration={100}>
                <div className="rounded-lg border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-800 text-sm" data-testid="ca-corner-detail-meta">
                  <div className="flex justify-between items-center px-4 py-2"><span className="text-slate-500">Marque</span><span className="font-medium text-slate-700 dark:text-slate-200">{fmtText(detailCorner.marque)}</span></div>
                  <div className="flex justify-between items-center px-4 py-2"><span className="text-slate-500">Enseigne</span><span className="font-medium text-slate-700 dark:text-slate-200">{fmtText(detailCorner.enseigne)}</span></div>
                  <div className="flex justify-between items-center px-4 py-2"><span className="text-slate-500">Ville</span><span className="font-medium text-slate-700 dark:text-slate-200">{fmtText(detailCorner.city)}</span></div>
                  <div className="flex justify-between items-center px-4 py-2"><span className="text-slate-500">Nom du DR</span><span className="font-medium text-slate-700 dark:text-slate-200">{fmtText(detailCorner.nom_dr)}</span></div>
                  <div className="flex justify-between items-center px-4 py-2"><span className="text-slate-500">Business Manager</span><span className="font-medium text-slate-700 dark:text-slate-200">{fmtText(detailCorner.nom_bm)}</span></div>
                  <div className="flex justify-between items-center px-4 py-2"><span className="text-slate-500 flex items-center gap-1">Mois du CA<InfoTip text="Mois du CA importé : dernier mois clôturé (CA M-1)." /></span><span className="font-medium text-slate-700 dark:text-slate-200">{fmtText(detailCorner.ca_n_month_label)}</span></div>
                  <div className="flex justify-between items-center px-4 py-2"><span className="text-slate-500">Date 1er contrat</span><span className="font-medium text-slate-700 dark:text-slate-200">{fmtText(detailCorner.date_contrat)}</span></div>
                  <div className="flex justify-between items-center px-4 py-2"><span className="text-slate-500 flex items-center gap-1">Date FDC<InfoTip text="Date de fin de contrat (FDC) : échéance du contrat du corner." /></span><span className="font-medium text-slate-700 dark:text-slate-200">{fmtText(detailCorner.date_fdc)}</span></div>
                  <div className="flex justify-between items-center px-4 py-2"><span className="text-slate-500">Surface du corner</span><span className="font-medium text-slate-700 dark:text-slate-200">{fmtSurface(detailCorner.surface_m2)}</span></div>
                  <div className="flex justify-between items-center px-4 py-2"><span className="text-slate-500">Taux de commission</span><span className="font-medium text-slate-700 dark:text-slate-200">{fmtTaux(detailCorner.taux_commission)}</span></div>
                  <div className="flex justify-between items-center px-4 py-2"><span className="text-slate-500 flex items-center gap-1">Moyenne qualité<InfoTip text="Moyenne des audits qualité du corner. Code couleur : vert ≥ 95 %, orange 90–94,99 %, rouge < 90 %. Survolez le badge pour voir le détail des audits (mois et scores)." /></span><QualityBadge value={detailCorner.quality_avg} detail={detailCorner.quality_detail} /></div>
                  <div className="flex justify-between items-center px-4 py-2"><span className="text-slate-500 flex items-center gap-1">Achat (Foodcost)<InfoTip text="Ratio d'achat (Foodcost). Objectif : ≤ 24 %. Vert si ≤ 24 %, orange clair au-dessus." /></span><RatioBadge value={detailCorner.foodcost} objective={RATIO_OBJECTIVES.foodcost} /></div>
                  <div className="flex justify-between items-center px-4 py-2"><span className="text-slate-500 flex items-center gap-1">Ratio Saumon<InfoTip text="Ratio Saumon. Objectif : ≤ 7,50 %. Vert si ≤ 7,50 %, orange clair au-dessus." /></span><RatioBadge value={detailCorner.ratio_saumon} objective={RATIO_OBJECTIVES.ratio_saumon} digits={2} /></div>
                  <div className="flex justify-between items-center px-4 py-2"><span className="text-slate-500 flex items-center gap-1">Taux de casse<InfoTip text="Taux de casse. Objectif : ≤ 10 %. Vert si ≤ 10 %, orange clair au-dessus." /></span><RatioBadge value={detailCorner.taux_casse} objective={RATIO_OBJECTIVES.taux_casse} /></div>
                  <div className="flex justify-between items-center px-4 py-2"><span className="text-slate-500 flex items-center gap-1">Rendement saumon<InfoTip text="Rendement saumon moyen (€/kg). Objectif : ≥ 230 €/kg. Vert si ≥ 230, orange en dessous." /></span><RatioBadge value={detailCorner.rendement_saumon} objective={230} higherIsBetter suffix=" €/kg" digits={0} /></div>
                  <div className="flex justify-between items-center px-4 py-2"><span className="text-slate-500 flex items-center gap-1">Productivité horaire<InfoTip text="Productivité horaire moyenne (€/heure travaillée). Objectif : ≥ 60 €/h. Vert si ≥ 60, orange en dessous." /></span><RatioBadge value={detailCorner.productivite} objective={60} higherIsBetter suffix=" €/h" digits={1} /></div>
                </div>
                </TooltipProvider>
              </div>
              <div>
                <p className="text-sm font-semibold mb-2 flex items-center gap-1">
                  Historique qualité (audits)
                  <InfoTip text="Évolution mois par mois de la note d'audit qualité. Points verts ≥ 95 %, orange 90–94,99 %, rouge < 90 %. Lignes de repère à 90 % et 95 %." />
                </p>
                <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3" data-testid="ca-corner-detail-quality-history">
                  <QualityHistoryChart detail={detailCorner.quality_detail} average={detailCorner.quality_avg} />
                </div>
              </div>
              {detailCorner.history && detailCorner.history.length > 0 && (
                <div>
                  <p className="text-sm font-semibold mb-2">Historique des imports mensuels</p>
                  <div className="max-h-48 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-800">
                    {detailCorner.history.map((h, i) => (
                      <div key={i} className="flex justify-between px-4 py-2 text-sm">
                        <span className="text-slate-500">{h.period}</span>
                        <span className="font-mono font-medium">{fmtEur(h.ca)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
    </AppLayout>
  );
}
